<script setup lang="ts">
/**
 * 代管主機詳情（`/hosts/:id`——本 SPA 第一條 param 路由）。
 *
 * - 10 秒輪詢主機＋最近任務（`lib/poll.ts`）；資源曲線另用 60 秒一輪（288 點不必秒秒重抓）。
 *   **編輯區不隨輪詢覆寫**（否則打字打到一半被蓋掉），表單只在首次載入／存檔／取消時同步。
 * - 修改與重發註冊碼＝admin 以上（後端 `require_admin` 是真閘門，這裡是 UX）。
 * - 站台列動作（批B／C2／D）分三級：升級／備份／診斷 operator 可發；停站／復站／補簽憑證／
 *   vendor SSO 要 admin；回收要 superadmin＋打字輸入完整網域。
 * - `detail.recent_jobs` 後端仍固定回空陣列 → 最近任務改抓 `GET /jobs?host_id=`。
 */
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AgentConfigEditor from '@/components/AgentConfigEditor.vue'
import EnrollCodeCard from '@/components/EnrollCodeCard.vue'
import MetricsChart from '@/components/MetricsChart.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import UiButton from '@/components/UiButton.vue'
import UiMeter from '@/components/UiMeter.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type {
  EnrollInfo,
  HostJob,
  HostService,
  HostServiceAction,
  HostServiceIn,
  HostSite,
  ManagedHostDetail,
  ManagedHostPatch,
  MetricsSeries,
  RelicensePreview,
  SsoGrant,
  SsoTicket,
  UpgradePlan,
  UpgradeRun,
} from '@/api/hosting'
import { mountPath } from '@/api/hosting'
import {
  agentStaleLevel,
  alertDetailText,
  alertKindLabel,
  certLevel,
  fmtBytes,
  fmtCapacity,
  healthLabel,
  hostStatusClass,
  hostStatusLabel,
  pubDetailText,
  pubStateChip,
  pubStateLabel,
  severityLabel,
  siteStateClass,
  siteStateLabel,
  tlsMissing,
  tlsStateLabel,
} from '@/lib/fleet'
import {
  fmtDuration,
  jobElapsed,
  jobKindLabel,
  jobPhaseLabel,
  jobStateClass,
  jobStateLabel,
} from '@/lib/jobs'
import { featuresOf, hasFeature } from '@/lib/hostingProfile'
import {
  HOST_SERVICE_ACTION_LABELS,
  hostServiceActions,
  hostServiceInputError,
  hostServiceIssueText,
  hostServiceSitesText,
  hostServiceStateClass,
  hostServiceStateLabel,
} from '@/lib/hostServices'
import type { FeatureKey } from '@/lib/hostingProfile'
import { mixedBatchGroups, mixedBatchMessage } from '@/lib/mixedBatch'
import {
  RUN_SOURCE_LABEL,
  WEEKDAYS,
  defaultRunAtInput,
  defaultWindow,
  fmtInTz,
  isRunActive,
  itemResultText,
  itemStatus,
  moveItem,
  orderByPreferred,
  positionText,
  runAtProblem,
  runProgress,
  runStatus,
  tzInputToIso,
  upgradeErrorText,
  windowSummary,
} from '@/lib/upgradeSchedule'
import { usePolling } from '@/lib/poll'
import { safeHttpUrl } from '@/lib/safeUrl'
import { fmtSeen, staleLevel } from '@/lib/staleness'
import { useHostingRoute } from '@/lib/hostingRoutes'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

/**
 * 代管路由名的視角分流（批99-B）：我方員工＝`host-detail`，代理商＝`reseller-host-detail`。
 * 本頁兩個視角共用同一支元件，所以每個連結都得問一次自己現在是誰。
 */
const hr = useHostingRoute()

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()

/**
 * 代管維運權限（批99-B）。
 *
 * 我方 admin 以上，**或代理商**——主人裁示 9「代理商完整維運」：主機／註冊碼／開站／
 * 停站／復站／升級／回收／診斷／備份／補簽憑證全開，範圍限自己的主機（後端一律加
 * `reseller_id == 我` 的條件，別家一律 404，前端不必也不該再判一次）。
 *
 * ★ `auth.isAdminOrAbove` 對代理商恆為 false（`reseller` 不在 `ROLE_RANK` 裡、rank 0），
 *   所以每一處維運入口都要顯式加上 `|| isReseller`；漏一處＝代理商看得到主機卻按不動。
 */
const canManage = computed(() => auth.isAdminOrAbove || auth.isReseller)
//: 批102 V4：重發註冊碼是**我方專屬**（代理商 realm 那支端點已拔除）。
const canReissueEnroll = computed(() => auth.isAdminOrAbove && !auth.isReseller)
/**
 * 回收站台（不可逆）。我方是 superadmin 專屬；代理商對**自己 VM 上自己客戶的站**放行
 * （裁示 B.7-3）——那是他自己的機器，我方不該當他的守門員。
 */
const canDecommission = computed(() => auth.isSuperadmin || auth.isReseller)
/**
 * 「以供應商身分登入」（vendor SSO）——**我方專屬**（裁示 B.7-1，不開給代理商）。
 * 代理商 realm 後端根本沒有 `sso-ticket`／`sso-grants` 兩支端點，整塊不渲染。
 */
const canSso = computed(() => auth.isAdminOrAbove && !auth.isReseller)
/**
 * 主機設定／改站台設定（批112）——**我方 admin 專屬**。
 *
 * 代理商 realm 後端沒有 `PUT /hosts/{id}/agent-config` 也沒有
 * `POST /sites/{id}/config`（`services/jobs.RESELLER_KINDS` 不含 `set_site_config`
 * ——能改客戶站的 `.env` 就能把站台指到別的中台去）。所以這裡整塊不渲染。
 * ★ 唯讀的**遮罩顯示**仍給代理商看（`host_detail` 兩個 realm 共用同一份投影），
 *   只是沒有〈儲存〉鈕。
 */
const canAgentConfig = computed(() => auth.isAdminOrAbove && !auth.isReseller)
const canSiteConfig = canAgentConfig
/**
 * 主機服務（批169）的安裝／升級／移除——**我方 admin 專屬**（裁示 51：代理商自帶的主機也只有我方
 * 能派，代理商 realm 沒有派工端點）。卡片本身仍給代理商看（狀態是 `host_detail` 共用的同一份投影），
 * 只是沒有按鈕。
 */
const canHostService = computed(() => auth.isAdminOrAbove && !auth.isReseller)

const hostId = computed(() => String(route.params.id ?? ''))
const detail = ref<ManagedHostDetail | null>(null)
const loading = ref(true)
const loadError = ref('')
const editError = ref('')
const busy = ref(false)

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  // 批141：`{code, message}` 形狀（`site_config_unsupported`、`mixed_batch`…）優先顯示中文說明。
  const m = (d as { message?: unknown }).message
  return typeof m === 'string' ? m : JSON.stringify(d)
}

/**
 * 這一站的產品支援某個代管功能嗎（批141：站台投影的 `hosting_features`）。
 *
 * - `null`＝該產品沒有有效的代管設定 → 一律不支援（與後端 fail-closed 同方向）；
 * - `undefined`＝**舊後端**根本沒這一欄 → 照舊顯示（否則升級過渡期整片按鈕憑空消失）。
 */
function siteFeature(s: HostSite | null | undefined, key: FeatureKey): boolean {
  if (!s) return false
  if (s.hosting_features === undefined) return true
  return hasFeature(s.hosting_features, key)
}

// ===========================================================================
// 站台動作（批B／C2／D）
//
// 全部走「派一個 job」——UI 不直接改站台狀態，狀態一律由 agent 回報。動作按鈕的
// 角色 gate 見 `siteActions()`；後端 fail-closed，這裡只是不給按。
// ===========================================================================

const actSite = ref<HostSite | null>(null)
const actOpen = ref(false)
/** 正在跑的動作 key（同時只允許一個，避免連點派兩次工）。 */
const actBusy = ref('')
const grants = ref<SsoGrant[]>([])
const ssoTicket = ref<SsoTicket | null>(null)
// 回收：在同一個對話框內切成確認面板（打字輸入完整網域）
const decomOpen = ref(false)
const decomDomain = ref('')
const decomDays = ref('')

/** 批次升級的勾選（site id）。 */
const pickedSites = ref<string[]>([])
const batchVersion = ref('')
const batchPlugins = ref(false)
const batchBusy = ref(false)

/** 可批次升級的站：有佈更新器、且不在回收流程中。 */
function upgradable(s: HostSite): boolean {
  return s.updater_deployed && s.state !== 'decommissioned' && s.state !== 'decommissioning'
}
const upgradableSites = computed(() => (detail.value?.sites ?? []).filter(upgradable))
const allPicked = computed(
  () => upgradableSites.value.length > 0 && pickedSites.value.length === upgradableSites.value.length,
)
function togglePick(id: string) {
  pickedSites.value = pickedSites.value.includes(id)
    ? pickedSites.value.filter((x) => x !== id)
    : [...pickedSites.value, id]
}
function toggleAllPicks() {
  pickedSites.value = allPicked.value ? [] : upgradableSites.value.map((s) => s.id)
}

async function runBatchUpgrade() {
  if (!pickedSites.value.length) return
  const ok = await dialogs.confirm({
    title: '批次升級',
    message: `升級 ${pickedSites.value.length} 個站台${batchVersion.value.trim() ? `到 ${batchVersion.value.trim()}` : '到最新版'}？全部代管站會依序一次一站；某站失敗會略過並告警，回滾也失敗時才暫停。${batchPlugins.value ? '會一併更新擴充套件。' : ''}`,
    confirmText: '開始升級',
  })
  if (!ok) return
  batchBusy.value = true
  try {
    const out = await hosting.batchUpgradeHostSites(
      pickedSites.value,
      batchVersion.value.trim() || null,
      batchPlugins.value,
    )
    pickedSites.value = []
    await Promise.all([poll.refresh(), loadPlan()])
    const skipped = out.skipped.length
      ? `\n\n略過 ${out.skipped.length} 個：\n${out.skipped.map((s) => `・${s.slug}——${s.reason}`).join('\n')}`
      : ''
    await dialogs.notify({
      title: '已排入依序更新佇列',
      message: `已排入依序更新佇列（${out.queued} 站），批次編號 ${out.batch_id}。進度見上方〈預約更新〉卡。${skipped}`,
    })
  } catch (e) {
    // 批141（裁示 11）：勾到不同產品或不同 channel → 409 `mixed_batch`，把分組講清楚。
    const groups = mixedBatchGroups(e)
    if (groups) {
      await dialogs.notify({
        title: '這批站台不能一起升級',
        message: mixedBatchMessage(groups, (k) => store.productName(k)),
      })
    } else {
      await dialogs.notify({ title: '操作失敗', message: errText(e, '批次升級失敗') })
    }
  } finally {
    batchBusy.value = false
  }
}

// ===========================================================================
// 預約更新（批192）：固定維護時段＋單次預約＋逐站進度
//
// 後端把「全部代管站依序一次一站」排成全域佇列；手動批次也是一種 run（source='batch'）。
// 這裡只負責顯示與下指令，順序、暫停規則、去重都在後端。
// ===========================================================================

/** 能操作預約更新：我方員工（含 operator）或代理商（後端另限自己的主機）。 */
const canSchedule = computed(() => auth.isStaff || auth.isReseller)

const plan = ref<UpgradePlan | null>(null)
const planError = ref('')
const planBusy = ref(false)

async function loadPlan() {
  if (!hostId.value) return
  try {
    plan.value = await hosting.getUpgradePlan(hostId.value)
    planError.value = ''
  } catch (e) {
    // 舊後端沒這支端點、或暫時讀不到：整張卡退成提示，不擋主機頁。
    plan.value = null
    planError.value = errText(e, '讀取預約更新失敗')
  }
}

const planTz = computed(() => plan.value?.timezone || 'Asia/Taipei')
const planDisabled = computed(() => !canSchedule.value || plan.value?.agent_supported === false)
const planDisabledNote = computed(() =>
  plan.value?.agent_supported === false
    ? `主機 agent 需升級到 ${plan.value.agent_min_version ?? '較新版本'} 以上才能預約更新。`
    : '',
)
/** 卡片上顯示的 run：未結束的在前，再接已結束的。 */
const planRuns = computed<UpgradeRun[]>(() => {
  const all = plan.value?.runs ?? []
  return [...all.filter(isRunActive), ...all.filter((r) => !isRunActive(r))]
})

async function cancelRun(r: UpgradeRun) {
  const running = r.status !== 'scheduled'
  const ok = await dialogs.confirm({
    title: running ? '停止這一輪更新' : '取消預約',
    message: running
      ? '會停止派送後面還沒開始的站台；正在更新的那一站會跑完。確定嗎？'
      : `取消 ${fmtInTz(r.run_at, planTz.value)} 的預約更新？`,
    confirmText: running ? '停止' : '取消預約',
    danger: true,
  })
  if (!ok) return
  planBusy.value = true
  try {
    await hosting.cancelUpgradeRun(hostId.value, r.id)
    await loadPlan()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: upgradeErrorText(e) ?? errText(e, '取消失敗') })
  } finally {
    planBusy.value = false
  }
}

/** 站台 id → slug（預約 modal 的清單用）。 */
function siteSlug(id: string): string {
  return (detail.value?.sites ?? []).find((s) => s.id === id)?.slug ?? id
}

// --- 單次預約 modal ---
const onceOpen = ref(false)
const onceAt = ref('')
const onceOrder = ref<string[]>([])
const oncePicked = ref<string[]>([])
const oncePlugins = ref(false)
const onceMax = ref('180')
const onceBusy = ref(false)
const onceError = ref('')

function openOnce() {
  const ids = upgradableSites.value.map((s) => s.id)
  onceAt.value = defaultRunAtInput(planTz.value)
  onceOrder.value = orderByPreferred(ids, plan.value?.window?.site_order ?? [])
  oncePicked.value = [...ids]
  // 代管預設連擴充套件一起更新（主人裁示）；有固定維護時段時沿用它的選擇。
  oncePlugins.value = plan.value?.plugins_supported === true && (plan.value?.window?.include_plugins ?? true)
  onceMax.value = '180'
  onceError.value = ''
  onceOpen.value = true
}
function toggleOncePick(id: string) {
  oncePicked.value = oncePicked.value.includes(id)
    ? oncePicked.value.filter((x) => x !== id)
    : [...oncePicked.value, id]
}
const onceSites = computed(() => onceOrder.value.filter((id) => oncePicked.value.includes(id)))

async function submitOnce() {
  onceError.value = ''
  const iso = tzInputToIso(onceAt.value, planTz.value)
  const bad = runAtProblem(iso)
  if (bad) {
    onceError.value = bad
    return
  }
  if (!onceSites.value.length) {
    onceError.value = '請至少勾選一個站台。'
    return
  }
  const max = Number(onceMax.value)
  if (!Number.isInteger(max) || max < 30 || max > 720) {
    onceError.value = '最長執行時間要是 30 到 720 之間的整數（分鐘）。'
    return
  }
  onceBusy.value = true
  try {
    await hosting.createUpgradeRun(hostId.value, {
      run_at: iso as string,
      site_ids: onceSites.value,
      include_plugins: oncePlugins.value && plan.value?.plugins_supported === true,
      max_minutes: max,
    })
    onceOpen.value = false
    await loadPlan()
  } catch (e) {
    onceError.value = upgradeErrorText(e, plan.value?.agent_min_version) ?? errText(e, '建立預約失敗')
  } finally {
    onceBusy.value = false
  }
}

// --- 固定維護時段 modal ---
const winOpen = ref(false)
const win = ref(defaultWindow())
const winMax = ref('180')
const winBusy = ref(false)
const winError = ref('')

function openWindow() {
  const cur = plan.value?.window ?? defaultWindow()
  const ids = upgradableSites.value.map((s) => s.id)
  win.value = {
    ...cur,
    weekdays: [...cur.weekdays],
    include_plugins: plan.value?.plugins_supported === true ? cur.include_plugins : false,
    site_order: orderByPreferred(ids, cur.site_order),
  }
  winMax.value = String(cur.max_minutes)
  winError.value = ''
  winOpen.value = true
}
function toggleWeekday(d: number) {
  win.value.weekdays = win.value.weekdays.includes(d)
    ? win.value.weekdays.filter((x) => x !== d)
    : [...win.value.weekdays, d].sort((a, b) => a - b)
}

async function submitWindow() {
  winError.value = ''
  if (win.value.enabled && !win.value.weekdays.length) {
    winError.value = '啟用時請至少選一個星期。'
    return
  }
  if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(win.value.time)) {
    winError.value = '請填寫維護時間（HH:MM）。'
    return
  }
  const max = Number(winMax.value)
  if (!Number.isInteger(max) || max < 30 || max > 720) {
    winError.value = '最長執行時間要是 30 到 720 之間的整數（分鐘）。'
    return
  }
  winBusy.value = true
  try {
    const body = { ...win.value, max_minutes: max }
    delete body.updated_at
    await hosting.setUpgradeWindow(hostId.value, body)
    winOpen.value = false
    await loadPlan()
  } catch (e) {
    winError.value = upgradeErrorText(e, plan.value?.agent_min_version) ?? errText(e, '儲存維護時段失敗')
  } finally {
    winBusy.value = false
  }
}

/** 開站精靈帶著主機 id 進去（第二步會預選這台）。 */
function goProvision() {
  void router.push({ name: hr('provision'), query: { host: hostId.value } })
}

function openSiteActions(s: HostSite) {
  actSite.value = s
  actOpen.value = true
  decomOpen.value = false
  decomDomain.value = ''
  decomDays.value = ''
  ssoTicket.value = null
  grants.value = []
  // vendor SSO 稽核紀錄只有我方 admin 以上讀得到（operator 打會 403、代理商 realm 沒這支）。
  // 批141：產品不支援供應商登入的站也不載（後端對它的簽票一律 409）。
  if (canSso.value && siteFeature(s, 'vendor_sso')) {
    void hosting
      .listHostSiteSsoGrants(s.id)
      .then((rows) => {
        grants.value = rows
      })
      .catch(() => {})
  }
}
function closeSiteActions() {
  actOpen.value = false
  actSite.value = null
  ssoTicket.value = null
}

// --- 主機設定（批112；1.0.44 改成「掛設定樣板」）-----------------------------
//
// 一張卡＝一份掛在這台主機上的樣板。樣板本身在〈產品 › 設定樣板〉維護（跨主機共用），
// 這裡只做三件事：掛上來／改下發範圍／卸載，以及填值。
const agentConfigSaving = ref(false)

async function saveAgentConfig(namespace: string, values: Record<string, unknown>) {
  if (!hostId.value) return
  agentConfigSaving.value = true
  try {
    const out = await hosting.updateHostAgentConfig(hostId.value, { namespace, values })
    if (detail.value) detail.value.agent_config = out.agent_config
    await dialogs.notify({
      title: '已儲存主機設定',
      message: out.changed.length
        ? `已更新：${out.changed.join('、')}。新開的站會自動帶入；既有站台請到站台列按〈改設定〉。`
        : '沒有任何欄位變動。',
    })
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '儲存主機設定失敗') })
  } finally {
    agentConfigSaving.value = false
  }
}

/** 掛載視窗（新掛一份／改既有掛載的範圍共用同一個表單）。 */
const attachOpen = ref(false)
const attachBusy = ref(false)
const attachTemplateId = ref('')
/** null＝全部產品；非 null＝勾選清單（以這台自報支援的產品為選項）。 */
const attachProducts = ref<string[] | null>(null)
/** 非 null＝改既有掛載的範圍（樣板下拉鎖住）。 */
const attachEditing = ref<string | null>(null)

/**
 * 下發範圍可以勾的產品（批141）＝這台自報支援的產品 ∩ **吃主機設定**的產品
 * （產品代管設定的 `features.site_config`）。明列一個不吃設定的產品，後端會 422
 * `site_config_unsupported`——那等於以為設定會下發、實際上永遠不會。
 * 產品清單查不到的鍵（還沒登錄的產品）照舊列出：後端對未登錄的鍵也照收。
 */
const mountableProducts = computed<string[]>(() =>
  (detail.value?.agent_products ?? []).filter((k) => {
    const spec = store.productByKey(k)
    return !spec || hasFeature(featuresOf(spec.options), 'site_config')
  }),
)

/**
 * 這台主機上有沒有任何產品吃主機設定（批141-R）——沒有的話〈主機設定〉卡與〈＋ 掛載樣板〉
 * 整塊不出現（例如只跑不吃主機設定的產品的主機，掛了樣板也永遠不會下發）。
 *
 * - `agent_products == null`＝舊 agent 沒回報支援哪些產品 → 不知道，照舊顯示；
 * - 產品清單還沒載回來時 `mountableProducts` 對查不到的鍵照列 → 也照舊顯示
 *   （寧可多顯示一下，也不要把能用的功能藏掉）。
 * - 已經掛了樣板的主機不在此限：卡片照樣出現（值要看得到、也要能卸載），只收掉〈＋ 掛載樣板〉
 *   並加一行說明。
 */
const hostTakesSiteConfig = computed(
  () => detail.value?.agent_products == null || mountableProducts.value.length > 0,
)

/** 還沒掛上來的樣板（掛過的不再出現在下拉裡）。 */
const attachableTemplates = computed(() => {
  const mounted = new Set((detail.value?.agent_config ?? []).map((b) => b.template_id))
  return store.agentConfigTemplates.filter((t) => !mounted.has(t.id))
})

function openAttach() {
  attachEditing.value = null
  attachTemplateId.value = attachableTemplates.value[0]?.id ?? ''
  attachProducts.value = null
  attachOpen.value = true
  void store.loadAgentConfigTemplates().catch(() => {})
}

function openScope(block: hosting.AgentConfigBlock) {
  attachEditing.value = block.template_id
  attachTemplateId.value = block.template_id ?? ''
  attachProducts.value = block.products == null ? null : [...block.products]
  attachOpen.value = true
}

function toggleAttachProduct(key: string) {
  const cur = attachProducts.value
  if (cur == null) {
    // 從「全部」切成明確清單：先帶入這台可掛的全部，再把點到的那個拿掉。
    attachProducts.value = mountableProducts.value.filter((k) => k !== key)
    return
  }
  attachProducts.value = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]
}

async function submitAttach() {
  if (!hostId.value || !attachTemplateId.value) return
  attachBusy.value = true
  try {
    const body = { products: attachProducts.value }
    const out = attachEditing.value
      ? await hosting.updateHostTemplate(hostId.value, attachEditing.value, body)
      : await hosting.attachHostTemplate(hostId.value, {
        template_id: attachTemplateId.value,
        ...body,
      })
    if (detail.value) detail.value.agent_config = out.agent_config
    attachOpen.value = false
  } catch (e) {
    // ★ 撞名（409）要把撞到的 env 名列出來——只說「衝突」的話操作者不知道改哪裡。
    const payload = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : null
    const names = payload && typeof payload === 'object' ? (payload as { env_names?: string[] }).env_names : undefined
    await dialogs.notify({
      title: '操作失敗',
      message: names?.length
        ? `這些環境變數名與既有掛載重複：${names.join('、')}。請改樣板的前綴或欄位代號。`
        : errText(e, '掛載設定樣板失敗'),
    })
  } finally {
    attachBusy.value = false
  }
}

async function detachTemplate(block: hosting.AgentConfigBlock) {
  if (!hostId.value || !block.template_id) return
  const ok = await dialogs.confirm({
    title: `卸載設定樣板「${block.name}」`,
    message:
      `同前綴（${block.namespace}）已經沒有其他掛載時，這台主機上該層已存的值會一起刪除。`
      + '已經下發到站台 .env 的鍵不會因此消失——要清掉請用站台列的〈改設定〉派工。',
    danger: true,
    confirmText: '卸載',
  })
  if (!ok) return
  try {
    const out = await hosting.detachHostTemplate(hostId.value, block.template_id)
    if (detail.value) detail.value.agent_config = out.agent_config
    if (out.removed_values.length) {
      await dialogs.notify({
        title: '已卸載',
        message: `一併清除了已存的值：${out.removed_values.join('、')}。`,
      })
    }
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '卸載設定樣板失敗') })
  }
}

/**
 * 這一站**可以經派工調整**的欄位（非秘密、非停用、且掛載範圍涵蓋這一站的產品）。
 *
 * ★ 1.0.44：白名單的單位是**完整環境變數名**（`env_key`），不是欄位鍵——欄位鍵在不同
 *   namespace 底下可以重複，單靠它指不出唯一一個欄位（後端 `provision_extra_env` 同）。
 */
function siteConfigFields(site: HostSite) {
  return (detail.value?.agent_config ?? [])
    .filter((b) => b.effective_products.includes(site.product))
    .flatMap((b) => b.fields)
    .filter((f) => !f.secret && f.env_key && !f.deprecated)
}

const siteConfigOpen = ref(false)
const siteConfigDraft = ref<Record<string, string>>({})
const siteConfigBusy = ref(false)

function openSiteConfig() {
  const s = actSite.value
  if (!s) return
  const draft: Record<string, string> = {}
  for (const f of siteConfigFields(s)) draft[f.env_key] = f.value == null ? '' : String(f.value)
  siteConfigDraft.value = draft
  siteConfigOpen.value = true
}

async function submitSiteConfig() {
  const s = actSite.value
  if (!s) return
  const values: Record<string, unknown> = {}
  for (const f of siteConfigFields(s)) {
    // ★ `String(...)` 不可省：Vue 3 對 `<input type="number">` 自動轉數字，
    //   直接 `.trim()` 會在使用者打字的那一刻 TypeError（批106 的根因）。
    const raw = String(siteConfigDraft.value[f.env_key] ?? '').trim()
    if (raw === '') continue
    values[f.env_key] =
      f.type === 'number' ? Number(raw) : f.type === 'bool' ? raw === 'true' : raw
  }
  if (!Object.keys(values).length) {
    await dialogs.notify({ title: '沒有要改的值', message: '至少填一個欄位。' })
    return
  }
  siteConfigBusy.value = true
  try {
    const job = await hosting.dispatchSiteConfig(s.id, values)
    siteConfigOpen.value = false
    await afterJob(job, '改設定')
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '派送改設定失敗') })
  } finally {
    siteConfigBusy.value = false
  }
}

interface SiteActionRow {
  key: string
  label: string
  desc: string
  variant?: 'primary' | 'danger'
  /** 非 null＝不能按的理由（畫面照樣列出來，讓人知道為什麼）。 */
  blocked: string | null
}

/** 目前這個站可見的動作（依角色與站台狀態）。 */
const siteActions = computed<SiteActionRow[]>(() => {
  const s = actSite.value
  if (!s) return []
  const gone = s.state === 'decommissioned' || s.state === 'decommissioning'
  // 批112 覆核 C1：**刻意停著的站不給改設定**——套用設定是 `compose up -d`，而停站是
  // `compose stop`（容器留著），`up -d` 會把整站拉回來，可是 nginx 仍掛 503、中台記錄
  // 仍是停用，而且 job 回 succeeded、健康輪詢也過，完全沒有訊號。後端也擋（409），
  // 這一道是讓人不必點下去才看到錯誤。
  const stopped = s.stopped_reason
    ? s.stopped_reason === 'trial_expired'
      ? '站台因試用到期已停用（請先復站）'
      : '站台已停用（請先復站）'
    : null
  const rows: SiteActionRow[] = [
    {
      key: 'update_site',
      label: '升級',
      desc: '下載更新包進該站 spool，由 host updater 驗簽→快照→套用→健康輪詢。',
      variant: 'primary',
      blocked: gone
        ? '站台已回收或正在回收'
        : s.updater_deployed
          ? null
          : '該站尚未佈署主機更新器（updater unit／spool）',
    },
    {
      key: 'run_backup',
      label: '執行備份',
      desc: '在該站跑一次 backup.sh（dump＋附件打包到備份目錄）。',
      blocked: gone ? '站台已回收或正在回收' : null,
    },
    {
      key: 'collect_diagnostics',
      label: '收集診斷',
      desc: 'compose logs 尾段＋df＋journalctl，截斷後上傳（唯讀，可重試 3 次）。',
      blocked: null,
    },
  ]
  if (canManage.value) {
    rows.push(
      {
        key: 'issue_cert',
        label: '補簽憑證',
        desc: 'certbot 重跑一次（BYO 網域指過來之後、或憑證壞掉時用）。',
        blocked: gone ? '站台已回收或正在回收' : null,
      },
      {
        key: 'stop_site',
        label: '停站',
        desc: 'compose stop＋503 維護頁＋停更新器。不會 down、不撤授權（復站不必重發證）。',
        blocked: gone ? '站台已回收或正在回收' : s.state === 'stopped' ? '站台已是停站狀態' : null,
      },
      {
        key: 'resume_site',
        label: '復站',
        desc: 'compose start＋還原 vhost＋健康輪詢。',
        blocked: gone ? '站台已回收或正在回收' : s.state === 'running' ? '站台正在運行中' : null,
      },
    )
  }
  // 批112：改站台設定（只有我方 admin＋這個產品真的宣告了可改欄位時才出現）
  // 批141：產品的代管設定不吃主機設定（`site_config` 關）→ 整列不出現（後端 409）。
  if (canSiteConfig.value && siteFeature(s, 'site_config')) {
    rows.push({
      key: 'set_site_config',
      label: '改設定',
      desc: '改該站 .env 的指定鍵值並重建容器（api 與 worker 都會重新讀取）。'
        + '金鑰類欄位不走這條路——那是主機層的設定。',
      blocked: gone
        ? '站台已回收或正在回收'
        : (stopped
          ?? (siteConfigFields(s).length
            ? null
            : '這台主機沒有掛載適用於這個產品的設定樣板')),
    })
  }
  // 批141：產品不支援供應商登入（`vendor_sso` 關）→ 整列不出現（後端 409）。
  if (canSso.value && siteFeature(s, 'vendor_sso')) {
    rows.push(
      {
        key: 'sso',
        label: '以供應商身分登入',
        desc: '簽一張 120 秒、一次性、綁本站的票，直接進該站後台（不佔用配額、站上不留我方帳號）。',
        // needs_setup 的站簽了也進不去（CS 端 403 setup_required、前端守衛導回精靈）——
        // 批80 批A1 起後端 issue_ticket 也擋，這裡先擋是為了不用點下去才看到錯誤。
        blocked: gone
          ? '站台已回收或正在回收'
          : !s.public_url
            ? '站台尚無對外網址'
            : s.needs_setup
              ? '站台尚未完成初始設定'
              : null,
      },
    )
  }
  if (canDecommission.value) {
    rows.push({
      key: 'decommission_site',
      label: '回收站台',
      desc: '最終備份 → 銷毀（唯一會 compose down 的路徑）→ 撤授權＋刪 DNS。不可逆。',
      variant: 'danger',
      blocked: s.state === 'decommissioned' ? '站台已回收' : null,
    })
  }
  return rows
})

// --- 主機服務（批169）--------------------------------------------------------
// ★ 服務名、中文名與預設 channel 都是 agent 回報的（`detail.host_services`），這裡不寫任何服務名字面。

/**
 * 卡片顯示條件：agent 回報了至少一個服務；或 agent 從沒回報過（`null`，版本太舊）而看的人能派工
 * ——那時要告訴他「升級 agent 才有主機服務可用」，不然他不知道這張卡為什麼不在。
 */
const showHostServices = computed(() => {
  const list = detail.value?.host_services
  if (list && list.length) return true
  return list == null && canHostService.value
})

const hsvcOpen = ref(false)
const hsvcTarget = ref<HostService | null>(null)
const hsvcAction = ref<HostServiceAction>('install')
const hsvcVersion = ref('')
const hsvcChannel = ref('')
const hsvcBusy = ref(false)
const hsvcError = computed(() =>
  hsvcAction.value === 'remove' ? null : hostServiceInputError(hsvcVersion.value, hsvcChannel.value),
)
const hsvcTitle = computed(() => {
  const svc = hsvcTarget.value
  if (!svc) return '主機服務'
  return `${HOST_SERVICE_ACTION_LABELS[hsvcAction.value]}${svc.label || svc.service}`
})

function openHostService(svc: HostService, action: HostServiceAction) {
  hsvcTarget.value = svc
  hsvcAction.value = action
  hsvcVersion.value = ''
  hsvcChannel.value = ''
  hsvcOpen.value = true
}

async function submitHostService() {
  const svc = hsvcTarget.value
  if (!svc || hsvcError.value) return
  const action = hsvcAction.value
  const title = hsvcTitle.value
  if (action === 'remove') {
    const ok = await dialogs.confirm({
      title,
      message:
        `會停止 ${svc.label || svc.service}、刪除它的 image 與版本目錄；各站的連線目錄保留。` +
        '\n\n移除之後，這台主機上的站台都用不到這個服務，直到重新安裝。確定移除？',
      confirmText: '移除',
      danger: true,
    })
    if (!ok) return
  }
  const body: HostServiceIn = { action }
  if (action !== 'remove') {
    const version = hsvcVersion.value.trim()
    const channel = hsvcChannel.value.trim()
    if (version) body.version = version
    if (channel) body.channel = channel
  }
  hsvcBusy.value = true
  try {
    const job = await hosting.dispatchHostService(hostId.value, svc.service, body)
    hsvcOpen.value = false
    await poll.refresh()
    await dialogs.notify({
      title: `已派出「${title}」`,
      message: `任務編號 ${job.job_id}，可在下方「最近任務」點進去看階段與 log。`,
    })
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, `${title}失敗`) })
  } finally {
    hsvcBusy.value = false
  }
}

/** 派一個站台 job 的共用尾巴：刷新任務卡＋提示去哪看進度。 */
async function afterJob(job: HostJob, what: string) {
  closeSiteActions()
  await poll.refresh()
  await dialogs.notify({
    title: `已派出「${what}」`,
    message: `任務編號 ${job.job_id}，可在下方「最近任務」點進去看階段與 log。`,
  })
}

async function runSiteAction(row: SiteActionRow) {
  const s = actSite.value
  if (!s || row.blocked) return
  if (row.key === 'decommission_site') {
    decomOpen.value = true
    return
  }
  if (row.key === 'sso') {
    await doVendorSso(s)
    return
  }
  if (row.key === 'set_site_config') {
    openSiteConfig()
    return
  }
  const ok = await dialogs.confirm({
    title: `${row.label} — ${s.slug}`,
    message: `${row.desc}\n\n確定對站台 ${s.domain ?? s.slug} 執行「${row.label}」？`,
    confirmText: row.label,
    danger: row.key === 'stop_site',
  })
  if (!ok) return
  actBusy.value = row.key
  try {
    let job: HostJob
    if (row.key === 'stop_site') job = await hosting.stopHostSite(s.id)
    else if (row.key === 'resume_site') job = await hosting.resumeHostSite(s.id)
    else if (row.key === 'issue_cert') job = await hosting.issueHostSiteCert(s.id)
    else job = await hosting.createHostJob(hostId.value, { kind: row.key, site_id: s.id })
    await afterJob(job, row.label)
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, `${row.label}失敗`) })
  } finally {
    actBusy.value = ''
  }
}

/** vendor SSO：取票後開新視窗；被瀏覽器擋掉就把網址列在對話框裡讓人自己點。 */
async function doVendorSso(s: HostSite) {
  const ok = await dialogs.confirm({
    title: '以供應商身分登入',
    message:
      '這會簽一張 120 秒、一次性的票並直接進入客戶站台的後台。\n\n⚠️ 這個動作會記錄在中台稽核（誰簽、哪一站、何時、票有沒有被用掉）——站台端查不到，只有這裡有。進站後不佔用配額、不留使用者與登入紀錄。',
    confirmText: '簽票並登入',
  })
  if (!ok) return
  actBusy.value = 'sso'
  try {
    const t = await hosting.issueHostSiteSsoTicket(s.id)
    ssoTicket.value = t
    // 批185（FE-01）：動態網址一律先過 `safeHttpUrl`（票的網址由 LS 組，這裡是縱深）。
    const ticketUrl = safeHttpUrl(t.url)
    const win = ticketUrl ? window.open(ticketUrl, '_blank', 'noopener') : null
    if (win) ssoTicket.value = null // 開起來了就不用再列網址
    grants.value = await hosting.listHostSiteSsoGrants(s.id).catch(() => grants.value)
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '簽發 SSO 票失敗') })
  } finally {
    actBusy.value = ''
  }
}

/** 最終備份保留天數的上下界（與後端 `DecommissionIn.keep_backup_days` 同一條：1–3650）。 */
const DECOM_DAYS_MIN = 1
const DECOM_DAYS_MAX = 3650

/**
 * 保留天數（批150）：留空＝預設 30 天；填了就必須是 1–3650 的整數。
 * 後端越界回 422（任何狀態變更之前）；這裡先擋，不用按下去才看到一串驗證錯誤。
 * ★ `type="number"` 的 v-model 會把值轉成數字（清空時是 `''`），兩種都要認。
 */
const decomDaysError = computed(() => {
  const raw: unknown = decomDays.value
  if (raw === '' || raw === null || raw === undefined) return ''
  const n = Number(raw)
  if (!Number.isInteger(n) || n < DECOM_DAYS_MIN || n > DECOM_DAYS_MAX) {
    return `保留天數請填 ${DECOM_DAYS_MIN}～${DECOM_DAYS_MAX} 的整數（留空＝預設 30 天）`
  }
  return ''
})

/** 回收：伺服器端也會比對 `confirm_domain`，這裡是體驗上的那一道。 */
const decomReady = computed(() => {
  const s = actSite.value
  return (
    !!s &&
    !!s.domain &&
    decomDomain.value.trim().toLowerCase() === s.domain.toLowerCase() &&
    !decomDaysError.value
  )
})

async function doDecommission() {
  const s = actSite.value
  if (!s || !decomReady.value) return
  actBusy.value = 'decommission_site'
  try {
    const job = await hosting.decommissionHostSite(s.id, {
      confirm_domain: decomDomain.value.trim(),
      keep_backup_days: decomDays.value ? Number(decomDays.value) : null,
    })
    decomOpen.value = false
    await afterJob(job, '回收站台')
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '回收失敗') })
  } finally {
    actBusy.value = ''
  }
}

// --- 編輯表單（與顯示區分離；輪詢不覆寫）---
interface EditForm {
  exclusive: boolean
  exclusive_customer_id: string
  /** 歸屬代理商（批99-B）；`''`＝我方自營。代理商視角不編輯這一欄。 */
  reseller_id: string
  max_sites: string
  status: string
  labels: string
  notes: string
}
const form = ref<EditForm>({
  exclusive: false,
  exclusive_customer_id: '',
  reseller_id: '',
  max_sites: '',
  status: 'pending',
  labels: '',
  notes: '',
})

function labelsText(labels: Record<string, unknown> | null): string {
  return labels && Object.keys(labels).length ? JSON.stringify(labels, null, 2) : ''
}
function fromDetail(d: ManagedHostDetail): EditForm {
  return {
    exclusive: d.exclusive,
    exclusive_customer_id: d.exclusive_customer_id ?? '',
    reseller_id: d.reseller_id ?? '',
    max_sites: d.max_sites == null ? '' : String(d.max_sites),
    status: d.status,
    labels: labelsText(d.labels),
    notes: d.notes ?? '',
  }
}
function syncForm() {
  if (detail.value) form.value = fromDetail(detail.value)
  editError.value = ''
}

const dirty = computed(() => {
  const d = detail.value
  if (!d) return false
  const base = fromDetail(d)
  const f = form.value
  return (
    f.exclusive !== base.exclusive ||
    f.exclusive_customer_id !== base.exclusive_customer_id ||
    f.reseller_id !== base.reseller_id ||
    f.max_sites !== base.max_sites ||
    f.status !== base.status ||
    f.labels.trim() !== base.labels.trim() ||
    f.notes !== base.notes
  )
})

// --- 最近任務（真資料；`detail.recent_jobs` 後端仍是空陣列）---
const jobs = ref<HostJob[]>([])
/** 輪詢時一起更新的「現在」——耗時欄靠它，才不會每次 render 都自己跳。 */
const jobsNow = ref(Date.now())

async function loadJobs() {
  if (!hostId.value) return
  try {
    // 批93-D：清單改成 keyset 分頁；這裡只要「最近 20 筆」，不續頁。
    jobs.value = (await hosting.listHostJobs({ hostId: hostId.value, limit: 20 })).items
    jobsNow.value = Date.now()
  } catch {
    // 任務清單抓不到不該讓整頁變成錯誤狀態（主機資料自己有 loadError）。
  }
}

async function load() {
  if (!hostId.value) return
  try {
    const fresh = await hosting.getManagedHost(hostId.value)
    const first = detail.value === null
    detail.value = fresh
    if (first) syncForm() // 之後的輪詢不動表單
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取主機失敗')
  } finally {
    loading.value = false
  }
  await Promise.all([loadJobs(), loadPlan()])
}

const poll = usePolling(load, 10_000)

// --- 資源曲線（批D）---
const metrics = ref<MetricsSeries | null>(null)
const metricsHours = ref(24)
const METRICS_RANGES = [
  { hours: 6, label: '6 小時' },
  { hours: 24, label: '24 小時' },
  { hours: 72, label: '3 天' },
  { hours: 168, label: '7 天' },
]

async function loadMetrics() {
  if (!hostId.value) return
  try {
    metrics.value = await hosting.getManagedHostMetrics(hostId.value, metricsHours.value)
  } catch {
    // 取樣資料抓不到就維持上一次的圖，不擋整頁。
  }
}
const metricsPoll = usePolling(loadMetrics, 60_000)
watch(metricsHours, () => void metricsPoll.refresh())

watch(hostId, () => {
  detail.value = null
  jobs.value = []
  plan.value = null
  metrics.value = null
  pickedSites.value = []
  loading.value = true
  void poll.refresh()
  void metricsPoll.refresh()
})
if (canManage.value) void store.ensureCustomers().catch(() => {})
// 歸屬顯示與「代理商」下拉的選項來源（批99-B，我方視角）。
// ⚠️ `GET /resellers` 是 superadmin 端點——admin 被 403 時 `ensureResellers()` 自己吞掉並
//    標記 `resellersDenied`，這時歸屬只能唯讀顯示（用主機列自帶的 `reseller_name`）。
if (!auth.isReseller) void store.ensureResellers().catch(() => {})
// 「支援產品」chip 要把產品鍵翻成中文（批99-A）。
// ⚠️ `GET /products` 是 admin 以上的端點——operator 會 403，`productName()` 退回原始鍵。
void store.ensureProducts().catch(() => {})
// 批112／1.0.44：「掛載樣板」下拉的來源。★ 代理商 realm 沒有這支端點（整塊也不渲染），
// 所以只在我方 realm 載；一律 catch——載不到就只是掛不了新樣板，既有掛載照樣顯示。
if (canAgentConfig.value) void store.ensureAgentConfigTemplates().catch(() => {})

async function save() {
  const d = detail.value
  if (!d) return
  let labels: Record<string, unknown> | null = null
  const raw = form.value.labels.trim()
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object')
      labels = parsed as Record<string, unknown>
    } catch {
      editError.value = '標籤必須是 JSON 物件，例如 {"tier":"prod","disk":"ssd"}'
      return
    }
  }
  if (form.value.status === 'retired' && d.status !== 'retired') {
    const ok = await dialogs.confirm({
      title: '退役主機',
      message: '退役後主機會從清單消失（可勾「顯示已退役」查看）。底下站台不會被回收，請先確認已搬遷。',
      danger: true,
      confirmText: '退役',
    })
    if (!ok) return
  }
  const patch: ManagedHostPatch = {
    exclusive: form.value.exclusive,
    exclusive_customer_id: form.value.exclusive ? form.value.exclusive_customer_id || null : null,
    max_sites: form.value.max_sites ? Number(form.value.max_sites) : null,
    labels,
    notes: form.value.notes.trim() || null,
    status: form.value.status as ManagedHostPatch['status'],
  }
  // 歸屬只有我方送得出（代理商 realm 的 `PUT` 不收這一欄）；`''` → null＝改回自營。
  // admin 讀不到代理商清單時表單上那一欄是唯讀的，這裡也就不會送（值恆等於現況）。
  if (!auth.isReseller && !store.resellersDenied) {
    patch.reseller_id = form.value.reseller_id || null
  }
  busy.value = true
  try {
    await hosting.updateManagedHost(hostId.value, patch)
    editError.value = ''
    await poll.refresh()
    syncForm()
    settingsOpen.value = false
  } catch (e) {
    editError.value = errText(e, '儲存失敗')
  } finally {
    busy.value = false
  }
}

// --- 重發註冊碼（admin+）---
const enroll = ref<EnrollInfo | null>(null)
const enrollOpen = ref(false)

async function reissue() {
  const ok = await dialogs.confirm({
    title: '重發註冊碼',
    message:
      '⚠️ 舊 agent 金鑰會「立即失效」、主機狀態打回「待註冊」：該台 VM 要重跑安裝指令才會恢復回報，期間所有心跳與盤點都會中斷。確定重發？',
    danger: true,
    confirmText: '重發註冊碼',
  })
  if (!ok) return
  busy.value = true
  try {
    enroll.value = await hosting.reissueEnrollCode(hostId.value)
    enrollOpen.value = true
    await poll.refresh()
    syncForm()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '重發註冊碼失敗') })
  } finally {
    busy.value = false
  }
}
async function closeEnroll() {
  enrollOpen.value = false
  enroll.value = null
}

// --- 換機：整批重簽授權（批80，admin+）------------------------------------
//
// 代管站沒有逐站轉移（客戶主機頁的「轉移」對代管站是隱藏的、後端 403）。VM 遷移／擴容／
// 重建換掉的是**主機 machine anchors**，那台上每一站的授權指紋會同時對不上，CS 端 OR 比對
// 全不等就把站台轉唯讀。所以入口在主機層，而且**一定先試算再確認**。

const reOpen = ref(false)
const rePreview = ref<RelicensePreview | null>(null)
const reBusy = ref(false)

/** 略過原因 → 中文（形狀由後端決定，未知值原樣顯示不吞資訊）。 */
const RE_SKIP_LABELS: Record<string, string> = {
  no_active_license: '沒有現行授權（開站中或已撤銷）',
  already_bound: '指紋已符合，不需重簽',
}
function reSkipLabel(reason: string): string {
  return RE_SKIP_LABELS[reason] ?? reason
}
/** 主機當前錨點的**鍵名**（值是綁機材料，畫面上刻意只列鍵）。 */
const reAnchorKeys = computed(() => Object.keys(rePreview.value?.anchors ?? {}).sort())

async function openRelicense() {
  reBusy.value = true
  try {
    rePreview.value = await hosting.relicenseHost(hostId.value, true)
    reOpen.value = true
  } catch (e) {
    await dialogs.notify({
      title: '無法試算',
      message:
        e instanceof ApiError && e.status === 409
          ? '這台主機還沒有任何 machine anchors（agent 尚未回報）。沒有錨點就重簽等於把授權的機器綁定拿掉，因此不予執行。'
          : errText(e, '重簽試算失敗'),
    })
  } finally {
    reBusy.value = false
  }
}

async function runRelicense() {
  const preview = rePreview.value
  if (!preview || preview.to_resign === 0) return
  reBusy.value = true
  try {
    const out = await hosting.relicenseHost(hostId.value, false)
    reOpen.value = false
    rePreview.value = null
    await poll.refresh()
    const lines = out.resigned.map((r) => `・${r.slug}：${r.new_license_id.slice(0, 8)}`).join('\n')
    await dialogs.notify({
      title: '已重簽授權',
      message: out.resigned.length
        ? `重簽 ${out.resigned.length} 張：\n${lines}\n\nagent 約 60 秒內會取回新證、寫檔並重啟該站 api。`
        : '沒有需要重簽的站台。',
    })
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '重簽授權失敗') })
  } finally {
    reBusy.value = false
  }
}

// --- 告警 ack ---
async function ack(alertId: string) {
  try {
    await hosting.ackHostAlert(alertId)
    await poll.refresh()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, 'ack 失敗') })
  }
}

// --- 顯示小工具 ---
/** JSONB（os_info／capacity／machine_anchors）→ 可直接印的純量鍵值（形狀由 agent 決定，容錯處理）。 */
function scalarEntries(obj: Record<string, unknown> | null): { k: string; v: string }[] {
  if (!obj) return []
  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([k, v]) => ({ k, v: String(v) }))
}
const mounts = computed(() => detail.value?.metrics?.mounts ?? [])
const forecast = computed(() => {
  const days = detail.value?.disk_forecast_days
  return days == null ? null : days < 1 ? '不到 1 天' : `約 ${days.toFixed(1)} 天`
})
function siteDomainText(s: HostSite): string {
  return s.domain || '—'
}
/** 公開網址那一行的提示：狀態＋原因（代碼翻成一句話）＋公開 port。 */
function pubTitle(s: HostSite): string {
  const parts = [`公開網址：${pubStateLabel(s.pub_state)}`]
  const why = pubDetailText(s.pub_detail)
  if (why) parts.push(why)
  if (s.pub_port) parts.push(`公開 port ${s.pub_port}`)
  return parts.join('｜')
}

// ===========================================================================
// 主機 image 盤點（批80 批B1；需 agent 0.2.8 以上）
//
// `detail.images` 是 agent `list_all_images()` 的輸出，LS 原封轉發。**null＝不知道**
// （舊 agent 沒回報過／docker 問不到）、`[]`＝這台確實沒有 image——兩者文案不同。
// ===========================================================================

/**
 * 記在 localStorage 的布林開關（摺疊狀態這種「每台瀏覽器各自記、不進 DB」的偏好）。
 * 無痕／封鎖 storage 時讀寫都會丟例外——一律吞掉，退回預設值就好。
 */
function usePersistedFlag(key: string, fallback: boolean) {
  let init = fallback
  try {
    const v = localStorage.getItem(key)
    if (v === '1' || v === '0') init = v === '1'
  } catch {
    /* storage 不可用：用預設 */
  }
  const flag = ref(init)
  watch(flag, (v) => {
    try {
      localStorage.setItem(key, v ? '1' : '0')
    } catch {
      /* 不記就是了 */
    }
  })
  return flag
}

/** 預設只顯示前幾列（一台 VM 動輒二三十筆，全開會把站台表推到看不見）。 */
const IMAGE_PREVIEW_ROWS = 10
const showAllImages = ref(false)
/** 整張 image 卡可縮合（主人裁示）：預設收起——卡頭保留「共 N 個、約 X」摘要，要細看再展開。 */
const imagesOpen = usePersistedFlag('ls.hostdetail.imagesOpen', false)

// --- 主機資訊（os_info／capacity／machine_anchors／標籤／備註）：可縮合的鍵值表 ---
//
// 原本用膠囊（chip）一顆顆排，十幾個鍵擠在頁首很難讀（主人裁示改掉）。改成兩欄鍵值表、
// 預設收起，切換列上只留一行摘要（發行版／docker／核心／記憶體）。

/** 鍵名→中文標籤；agent 沒列在這裡的鍵原樣印（agent 換版多送的欄位不會被吃掉）。 */
const INFO_LABELS: Record<string, string> = {
  arch: '架構',
  os_id: '作業系統',
  distro: '發行版',
  os_version: 'OS 版本',
  kernel: 'Kernel',
  python: 'Python',
  agent_version: 'agent 版本',
  docker_version: 'Docker',
  docker_compose_version: 'Compose',
  // ipo-agent 0.2.19（審查 AGT-05）：以 --insecure 註冊的主機才有這個鍵（值是警告文字）。
  agent_tls_verify: 'agent TLS 驗證',
  cpu_cores: 'CPU 核心',
  mem_total_mb: '記憶體（MB）',
  dmi: 'DMI UUID',
  docker: 'Docker engine-id',
  'machine-id': 'machine-id',
}
const infoOpen = usePersistedFlag('ls.hostdetail.infoOpen', false)
type InfoRow = { k: string; label: string; v: string; mono?: boolean; wide?: boolean }
const hostInfoRows = computed<InfoRow[]>(() => {
  const d = detail.value
  if (!d) return []
  const rows: InfoRow[] = []
  for (const e of scalarEntries(d.os_info)) rows.push({ k: `os-${e.k}`, label: INFO_LABELS[e.k] ?? e.k, v: e.v })
  for (const e of scalarEntries(d.capacity)) rows.push({ k: `cap-${e.k}`, label: INFO_LABELS[e.k] ?? e.k, v: e.v })
  for (const e of scalarEntries(d.machine_anchors))
    rows.push({ k: `an-${e.k}`, label: INFO_LABELS[e.k] ?? e.k, v: e.v, mono: true })
  const labelEntries = scalarEntries(d.labels)
  if (labelEntries.length)
    rows.push({ k: 'labels', label: '標籤', v: labelEntries.map((e) => `${e.k}=${e.v}`).join('、'), wide: true })
  if (d.notes) rows.push({ k: 'notes', label: '備註', v: d.notes, wide: true })
  return rows
})
/** 收起時的一行摘要（缺哪個鍵就少印哪段，不硬湊）。 */
const hostInfoSummary = computed(() => {
  const d = detail.value
  const os = (d?.os_info ?? {}) as Record<string, unknown>
  const cap = (d?.capacity ?? {}) as Record<string, unknown>
  const parts: string[] = []
  if (os.distro) parts.push(String(os.distro))
  if (os.docker_version) parts.push(`Docker ${os.docker_version}`)
  const cores = cap.cpu_cores ?? os.cpu_cores
  const mem = cap.mem_total_mb ?? os.mem_total_mb
  if (cores || mem) {
    const memGb = typeof mem === 'number' ? `${(mem / 1024).toFixed(1)} GB` : mem ? `${mem} MB` : ''
    parts.push([cores ? `${cores} 核` : '', memGb].filter(Boolean).join(' / '))
  }
  return parts.length ? parts.join(' · ') : `${hostInfoRows.value.length} 項`
})

// --- 主機設定：改成對話框（主人裁示），頁面本體不再放整張表單 ---
const settingsOpen = ref(false)
function openSettings() {
  syncForm()
  settingsOpen.value = true
}

/** 依 `size_bytes` 由大到小；解析不出來的（null）一律排最後。 */
const sortedImages = computed(() => {
  const rows = detail.value?.images
  if (!rows) return []
  return [...rows].sort((a, b) => (b.size_bytes ?? -1) - (a.size_bytes ?? -1))
})
const visibleImages = computed(() =>
  showAllImages.value ? sortedImages.value : sortedImages.value.slice(0, IMAGE_PREVIEW_ROWS),
)
/** 總量（`size_bytes` 缺值的那幾筆算不進去——寧可少報也別亂加）。 */
const imagesTotalBytes = computed(() =>
  sortedImages.value.reduce((sum, r) => sum + (r.size_bytes ?? 0), 0),
)
/** image 顯示名；dangling（repository 為 null）在模板另外標 chip。 */
function imageName(img: { repository: string | null; tag: string | null }): string {
  if (!img.repository) return '<none>'
  return img.tag ? `${img.repository}:${img.tag}` : img.repository
}

// --- 站台容器子列（批80 批B1）---

/** 展開的站台 id 集合（多站可同時展開；輪詢覆寫 detail 也不會收起來）。 */
const expandedSites = ref<string[]>([])
function toggleSiteRow(id: string) {
  expandedSites.value = expandedSites.value.includes(id)
    ? expandedSites.value.filter((x) => x !== id)
    : [...expandedSites.value, id]
}
/**
 * 有沒有容器可展開。已回收的站**刻意不給展開**：`host_sites.containers` 是誠實的歷史
 * （回收前跑著這些），但那份資料對「現在」沒有意義，展開只會誤導。
 */
function hasContainers(s: HostSite): boolean {
  return s.state !== 'decommissioned' && (s.containers?.length ?? 0) > 0
}
</script>

<template>
  <div class="page">
    <div class="crumb">
      <RouterLink :to="{ name: hr('hosts') }">← 代管主機</RouterLink>
    </div>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
    <div v-else-if="!detail" class="card">
      <div class="empty"><div class="empty-title">找不到這台主機</div><div class="empty-sub">{{ loadError || '可能已被刪除。' }}</div></div>
    </div>

    <template v-else>
      <p v-if="loadError" class="err">{{ loadError }}（顯示的是最後一次成功讀取的資料）</p>

      <!-- 頁首 -->
      <div class="card head">
        <div class="head-top">
          <h1>{{ detail.name }}</h1>
          <span class="status-pill" :class="hostStatusClass(detail.status)">{{ hostStatusLabel(detail.status) }}</span>
          <span v-if="detail.exclusive" class="chip excl">
            獨佔{{ detail.exclusive_customer_name ? `：${detail.exclusive_customer_name}` : '（未指定客戶）' }}
          </span>
          <span v-if="!detail.agent_enrolled" class="status-pill pending">agent 未註冊</span>
          <span class="spacer" />
          <!-- 三級配色（批81 需求 7，裁示 11）：中性設定＝secondary；重發註冊碼會讓舊碼
               當場失效（agent 拿舊碼再也註冊不了）＝warn；整批重簽是不可逆的換機＝danger。 -->
          <UiButton v-if="canManage" variant="secondary" @click="openSettings">
            主機設定
          </UiButton>
          <!-- ★ 批102 V4：**我方專屬**——代理商 realm 沒有 `/hosts/{id}/enroll-code`
               （註冊碼＝把 agent 掛進中台的全部憑證，能自助發碼就能認領別人的站）。
               同 `sso-ticket`／`sso-grants` 的處置：整塊不渲染，真打過去是 404。 -->
          <UiButton v-if="canReissueEnroll" variant="warn" :loading="busy" @click="reissue">
            重發註冊碼
          </UiButton>
          <UiButton v-if="canManage" variant="danger" :loading="reBusy" @click="openRelicense">
            換機：整批重簽授權
          </UiButton>
        </div>
        <div class="meta">
          <div class="mi"><span class="mk">對外 IP</span><span class="mv">{{ detail.public_ip || '—' }}</span></div>
          <div class="mi"><span class="mk">管理 FQDN</span><span class="mv">{{ detail.fqdn || '—' }}</span></div>
          <div class="mi"><span class="mk">供應商／區域</span><span class="mv">{{ [detail.provider, detail.region].filter(Boolean).join(' / ') || '—' }}</span></div>
          <!-- 歸屬（批99-B）：null＝愛+1 自營、非 null＝該代理商自建的 VM。
               代理商視角不畫——他看得到的主機恆是自己的（後端一律過濾），寫出來只是廢話。 -->
          <div v-if="!auth.isReseller" class="mi">
            <span class="mk">歸屬</span>
            <span class="mv">
              <span v-if="detail.reseller_id" class="chip owner">
                代理商{{ detail.reseller_name ? `：${detail.reseller_name}` : '' }}
              </span>
              <template v-else>愛+1 自營</template>
            </span>
          </div>
          <div class="mi"><span class="mk">agent 版本</span><span class="mv">{{ detail.agent_version || '—' }}</span></div>
          <!-- 批141：代管種類（agent 種類；註冊時定下、之後不可改；種類的門檻在〈產品 › 代管種類〉）。 -->
          <div class="mi">
            <span class="mk">代管種類</span>
            <span class="mv"><code class="mono">{{ detail.agent_kind || '—' }}</code></span>
          </div>
          <!-- 支援產品（批99-A）：agent 自報「這台裝得起哪些產品」。
               ★ 批114（裁示 ㉟）：舊 agent 的兜底規則拿掉了——**null＝尚未回報**，這台開不了站，
                 所以這裡直接指路去升 agent，而不是替它假設成某個產品。 -->
          <div class="mi">
            <span class="mk">支援產品</span>
            <span class="mv chips prodchips">
              <span v-for="k in detail.agent_products ?? []" :key="k" class="chip">{{ store.productName(k) }}</span>
              <span
                v-if="detail.agent_products == null"
                class="warn-note"
                title="agent 0.2.11 以前不會回報支援產品。中台不再替它假設，這台在升級 agent 之前無法開站。"
              >
                agent 尚未回報支援的產品，請升級 agent
              </span>
              <span v-else-if="!detail.agent_products.length" class="subtle">
                這台 agent 回報「目前一個產品都裝不起來」
              </span>
            </span>
          </div>
          <div class="mi"><span class="mk">agent 來源 IP</span><span class="mv">{{ detail.agent_last_ip || '—' }}</span></div>
          <div class="mi">
            <span class="mk">最後心跳</span>
            <span class="mv seen" :class="agentStaleLevel(detail.last_seen)">{{ fmtSeen(detail.last_seen) }}</span>
          </div>
          <div class="mi"><span class="mk">最後盤點</span><span class="mv">{{ fmtSeen(detail.last_inventory_at) }}</span></div>
          <div class="mi"><span class="mk">站數</span><span class="mv">{{ detail.sites_count }} / {{ detail.max_sites ?? '不限' }}</span></div>
        </div>
        <!-- 主機資訊（os_info／capacity／機器錨點／標籤／備註）：可縮合鍵值表，取代原本的一排膠囊 -->
        <div v-if="hostInfoRows.length" class="hinfo">
          <button type="button" class="hinfo-toggle" :aria-expanded="infoOpen" @click="infoOpen = !infoOpen">
            <span class="tri" :class="{ open: infoOpen }">▶</span>
            主機資訊
            <span class="subtle">{{ hostInfoSummary }}</span>
          </button>
          <div v-if="infoOpen" class="kv-grid">
            <template v-for="r in hostInfoRows" :key="r.k">
              <span class="kv-k">{{ r.label }}</span>
              <span class="kv-v" :class="{ mono: r.mono, wide: r.wide }">{{ r.v }}</span>
            </template>
          </div>
        </div>
      </div>

      <!-- 資源 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">資源水位</h3>
          <span class="spacer" />
          <span class="subtle">指標時間 {{ fmtSeen(detail.metrics_at) }}</span>
        </div>
        <div class="res-grid">
          <div class="meters">
            <UiMeter label="CPU" :pct="detail.cpu_pct" />
            <UiMeter label="RAM" :pct="detail.mem_used_pct" />
            <UiMeter label="磁碟" :pct="detail.disk_used_pct_max" title="各掛載點最高水位" />
            <p v-if="forecast" class="forecast">磁碟依近日成長推估 <strong>{{ forecast }}</strong> 後填滿</p>
            <p v-else class="subtle">磁碟成長趨勢：資料不足或未成長（不預警）。</p>
          </div>
          <div class="mounts">
            <div v-if="!mounts.length" class="subtle">agent 尚未回報掛載點。</div>
            <div v-else class="tbl-scroll" data-hscroll>
              <table v-card-table class="tbl card-table mini">
                <thead><tr><th>掛載點</th><th>水位</th><th>已用 / 總量</th></tr></thead>
                <tbody>
                  <tr v-for="m in mounts" :key="mountPath(m)">
                    <td><code class="mono">{{ mountPath(m) }}</code></td>
                    <td class="mcell"><UiMeter :pct="m.used_pct" /></td>
                    <td class="nowrap">
                      {{ fmtCapacity(m.used_mb, m.used_bytes) }} / {{ fmtCapacity(m.total_mb, m.total_bytes) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!--
        主機 image（批80 批B1）——回答「這台磁碟為什麼滿了」。
        同一台 VM 上多站共用 image（tag 不含站台名），加上升級留下的舊版與 dangling，
        佔的常常比資料本身多。排序＝size 由大到小（要看的就是大的那幾筆）。
      -->
      <div class="card">
        <div class="card-head">
          <button
            type="button"
            class="tri"
            :class="{ open: imagesOpen }"
            :title="imagesOpen ? '收起' : '展開'"
            :aria-expanded="imagesOpen"
            @click="imagesOpen = !imagesOpen"
          >
            ▶
          </button>
          <h3 class="section-title clickable" @click="imagesOpen = !imagesOpen">主機 image</h3>
          <span v-if="sortedImages.length" class="count">{{ sortedImages.length }}</span>
          <span class="spacer" />
          <span v-if="sortedImages.length" class="subtle">
            共 {{ sortedImages.length }} 個、約 {{ fmtBytes(imagesTotalBytes) }}
          </span>
          <span class="subtle">盤點時間 {{ fmtSeen(detail.last_inventory_at) }}</span>
        </div>
        <div v-if="imagesOpen && !sortedImages.length" class="empty">
          <div class="empty-sub">
            agent 尚未回報 image（需 agent 0.2.8 以上）。
          </div>
        </div>
        <template v-else-if="imagesOpen">
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table mini">
              <thead><tr><th>image</th><th>id</th><th>大小</th><th>建立時間</th></tr></thead>
              <tbody>
                <tr v-for="img in visibleImages" :key="img.id">
                  <td>
                    <span class="iname" :class="{ dangling: !img.repository }">{{ imageName(img) }}</span>
                    <span
                      v-if="!img.repository"
                      class="chip off"
                      title="沒有 tag 的 image（通常是升級後被取代的舊層）——磁碟滿了最常見的原因"
                    >
                      dangling
                    </span>
                  </td>
                  <td><code class="mono" :title="img.id">{{ img.id.slice(0, 12) }}</code></td>
                  <td class="nowrap">{{ img.size ?? '—' }}</td>
                  <td class="nowrap tsmall">{{ img.created_at ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="sortedImages.length > IMAGE_PREVIEW_ROWS" class="more-row">
            <button type="button" class="tab" @click="showAllImages = !showAllImages">
              {{ showAllImages ? '只顯示前 ' + IMAGE_PREVIEW_ROWS + ' 個' : '顯示全部（' + sortedImages.length + '）' }}
            </button>
          </div>
        </template>
      </div>

      <!--
        主機服務（批169）：全機共用一套的選配服務（agent 0.2.17+ 在全量心跳回報）。
        服務名、中文名、預設更新通道都來自 agent；按鈕只給我方 admin（代理商看得到狀態、派不了）。
      -->
      <div v-if="showHostServices" class="card">
        <div class="card-head">
          <h3 class="section-title">主機服務</h3>
          <span class="spacer" />
          <span v-if="detail.host_services_at" class="subtle">回報時間 {{ fmtSeen(detail.host_services_at) }}</span>
        </div>
        <div v-if="detail.host_services == null" class="empty">
          <div class="empty-sub">這台的 agent 還沒回報主機服務（需 ipo-agent 0.2.17 以上）——升級 agent 後就會出現可安裝的服務。</div>
        </div>
        <div v-else class="hsvc-list">
          <div v-for="svc in detail.host_services" :key="svc.service" class="hsvc">
            <div class="hsvc-head">
              <strong class="hsvc-name">{{ svc.label || svc.service }}</strong>
              <code class="mono subtle">{{ svc.service }}</code>
              <span class="status-pill" :class="hostServiceStateClass(svc)">{{ hostServiceStateLabel(svc) }}</span>
              <span class="spacer" />
              <template v-if="canHostService">
                <UiButton
                  v-for="action in hostServiceActions(svc)"
                  :key="action"
                  size="sm"
                  :variant="action === 'remove' ? 'danger' : action === 'install' ? 'primary' : 'secondary'"
                  @click="openHostService(svc, action)"
                >
                  {{ HOST_SERVICE_ACTION_LABELS[action] }}
                </UiButton>
              </template>
            </div>
            <div class="meta">
              <div class="mi"><span class="mk">版本</span><span class="mv">{{ svc.version || '—' }}</span></div>
              <div class="mi">
                <span class="mk">更新通道</span>
                <span class="mv"><code class="mono">{{ svc.channel || svc.default_channel || '—' }}</code></span>
              </div>
              <div class="mi" title="已接上這個服務的站台數／這台主機上的站台數">
                <span class="mk">已接上站台</span><span class="mv">{{ hostServiceSitesText(svc) }}</span>
              </div>
              <div v-if="svc.protocol != null" class="mi"><span class="mk">協定版本</span><span class="mv">{{ svc.protocol }}</span></div>
            </div>
            <ul v-if="svc.installed && svc.issues.length" class="hsvc-issues">
              <li v-for="(issue, idx) in svc.issues" :key="idx">{{ hostServiceIssueText(issue) }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 主機設定（批112）：中台下發給這台 VM 上站台的環境設定值 -->
      <!-- 批141-R：主機上沒有任何產品吃主機設定（產品代管設定的 `features.site_config` 全關）
           → 沒掛過樣板就整張卡不出現；已經掛過的照樣顯示（看值／卸載），但收掉〈＋ 掛載樣板〉。 -->
      <div
        v-if="(canAgentConfig && hostTakesSiteConfig) || detail.agent_config?.length"
        class="card"
      >
        <div class="card-head">
          <h3 class="section-title">主機設定（下發給站台）</h3>
          <span class="spacer" />
          <small class="muted">欄位由設定樣板宣告，中台不解讀語意</small>
          <UiButton
            v-if="canAgentConfig && hostTakesSiteConfig"
            size="sm"
            variant="secondary"
            @click="openAttach"
          >
            ＋ 掛載樣板
          </UiButton>
        </div>
        <p v-if="!hostTakesSiteConfig" class="muted cfg-note">
          這台主機上的產品都沒有開「主機設定下發」，下列設定目前不會下發給任何站台。
        </p>
        <AgentConfigEditor
          :blocks="detail.agent_config"
          :editable="canAgentConfig"
          :can-manage-templates="auth.isManagerOrAbove"
          :saving="agentConfigSaving"
          @save="saveAgentConfig"
          @detach="detachTemplate"
          @scope="openScope"
        />
      </div>

      <!-- 資源曲線（批D：inline SVG，不引圖表庫）-->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">資源曲線</h3>
          <span class="spacer" />
          <div class="tabs" data-hscroll>
            <button
              v-for="r in METRICS_RANGES"
              :key="r.hours"
              type="button"
              class="tab"
              :class="{ active: metricsHours === r.hours }"
              @click="metricsHours = r.hours"
            >
              {{ r.label }}
            </button>
          </div>
        </div>
        <MetricsChart
          :points="metrics?.points ?? []"
          :interval-seconds="metrics?.interval_seconds ?? 300"
          :height="200"
        />
      </div>

      <!-- 預約更新（批192）：固定維護時段、單次預約、逐站進度（含手動批次） -->
      <div class="card" data-testid="upgrade-plan-card">
        <div class="card-head">
          <h3 class="section-title">預約更新</h3>
          <span class="spacer" />
          <UiButton size="sm" :disabled="planDisabled || !plan" @click="openWindow">設定維護時段…</UiButton>
          <UiButton
            variant="primary"
            size="sm"
            :disabled="planDisabled || !plan || !upgradableSites.length"
            :title="upgradableSites.length ? '' : '沒有可升級的站台'"
            @click="openOnce"
          >
            預約更新…
          </UiButton>
        </div>

        <p v-if="planError" class="err">{{ planError }}</p>
        <div v-else-if="!plan" class="loading-wrap"><span class="spinner" /></div>
        <template v-else>
          <p v-if="planDisabledNote" class="upd-warn">{{ planDisabledNote }}</p>

          <div class="upd-window">
            <div class="mi">
              <span class="mk">固定維護時段</span>
              <span class="mv">{{ windowSummary(plan.window, planTz) }}</span>
            </div>
            <div v-if="plan.window?.enabled && plan.next_window_at" class="mi">
              <span class="mk">下一次</span>
              <span class="mv">{{ fmtInTz(plan.next_window_at, planTz) }}</span>
            </div>
          </div>

          <div v-if="!planRuns.length" class="empty">
            <div class="empty-sub">目前沒有待執行的預約，也沒有最近的更新紀錄。</div>
          </div>
          <div v-for="r in planRuns" :key="r.id" class="upd-run" :class="{ paused: r.status === 'paused' }">
            <div class="upd-run-head">
              <span class="status-pill" :class="runStatus(r.status).cls">{{ runStatus(r.status).label }}</span>
              <strong>{{ RUN_SOURCE_LABEL[r.source] }}</strong>
              <span class="subtle">{{ fmtInTz(r.run_at, planTz) }}</span>
              <span class="subtle">{{ r.items.length }} 站</span>
              <span v-if="r.include_plugins" class="subtle">含擴充套件</span>
              <span v-if="r.status === 'running' || r.status === 'paused'" class="subtle">
                進度 {{ runProgress(r).done }}／{{ runProgress(r).total }}
              </span>
              <span v-if="positionText(r)" class="subtle">{{ positionText(r) }}</span>
              <span v-if="r.created_by" class="subtle">預約人 {{ r.created_by.email }}</span>
              <span class="spacer" />
              <UiButton
                v-if="canSchedule && (r.status === 'scheduled' || r.status === 'running' || r.status === 'paused')"
                size="sm"
                variant="danger"
                :disabled="planBusy"
                @click="cancelRun(r)"
              >
                {{ r.status === 'scheduled' ? '取消預約' : '停止後續' }}
              </UiButton>
            </div>
            <p v-if="r.status === 'paused'" class="upd-warn">
              這一輪已暫停：有站台更新失敗且回滾也沒有成功，或任務逾時，需要人工檢查該站後處理。後面的站不會繼續派送。
            </p>
            <ol class="upd-items">
              <li v-for="it in r.items" :key="it.site_id" class="upd-item">
                <code class="mono">{{ it.slug }}</code>
                <span class="status-pill" :class="itemStatus(it.status).cls">{{ itemStatus(it.status).label }}</span>
                <span v-if="itemResultText(it)" class="subtle upd-res">{{ itemResultText(it) }}</span>
                <RouterLink v-if="it.job_id" class="jlink" :to="{ name: hr('job-detail'), params: { jobId: it.job_id } }">任務</RouterLink>
              </li>
            </ol>
          </div>
        </template>
      </div>

      <!-- 站台 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">站台</h3>
          <span class="count">{{ detail.sites.length }}</span>
          <span class="spacer" />
          <UiButton v-if="canManage" variant="primary" size="sm" @click="goProvision">
            ＋ 開新站台
          </UiButton>
        </div>

        <!-- 批次升級（operator 也可發 update_site）-->
        <div v-if="upgradableSites.length" class="batchbar">
          <label class="cb">
            <input type="checkbox" :checked="allPicked" @change="toggleAllPicks" />
            全選可升級（{{ upgradableSites.length }}）
          </label>
          <input v-model="batchVersion" class="ver sm" placeholder="版本（留空＝最新）" />
          <label class="cb" :title="plan && !plan.plugins_supported ? '這台主機或產品不支援順帶更新擴充套件' : ''">
            <input v-model="batchPlugins" type="checkbox" :disabled="plan != null && !plan.plugins_supported" />
            一併更新擴充套件
          </label>
          <UiButton
            variant="primary"
            size="sm"
            :disabled="!pickedSites.length"
            :loading="batchBusy"
            @click="runBatchUpgrade"
          >
            批次升級（{{ pickedSites.length }}）
          </UiButton>
          <span class="subtle">全部代管站依序一次一站；某站失敗會略過並告警，回滾也失敗時才暫停。</span>
        </div>

        <div v-if="!detail.sites.length" class="empty">
          <div class="empty-sub">
            這台主機上沒有站台。{{ canManage ? '按右上「＋ 開新站台」走五步精靈。' : '請管理員開站。' }}
          </div>
        </div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr>
                <th class="pick-h"></th>
                <th>站台</th><th>網域</th><th>客戶</th><th>狀態</th><th>版本</th>
                <th>port</th><th>健康</th><th>憑證到期</th><th>備份</th><th>標記</th><th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="s in detail.sites" :key="s.id">
              <tr>
                <td class="pick">
                  <input
                    type="checkbox"
                    :checked="pickedSites.includes(s.id)"
                    :disabled="!upgradable(s)"
                    :title="upgradable(s) ? '勾選以批次升級' : '未佈更新器或已回收，不能遠端升級'"
                    @change="togglePick(s.id)"
                  />
                </td>
                <td>
                  <div class="slugrow">
                    <!--
                      展開三角＝容器明細（批80 批B1）。表已經 12 欄，不再加欄——
                      容器是「深一層」的資料，放子列比擠成第 13 欄好讀。
                    -->
                    <button
                      v-if="hasContainers(s)"
                      type="button"
                      class="tri"
                      :class="{ open: expandedSites.includes(s.id) }"
                      :title="expandedSites.includes(s.id) ? '收起容器明細' : `展開容器明細（${s.containers.length} 個）`"
                      @click="toggleSiteRow(s.id)"
                    >
                      ▶
                    </button>
                    <span v-else class="tri ph" aria-hidden="true"></span>
                    <div class="hostcell">
                      <!-- 批151 C7：站台詳情只有我方員工的路由（代理商 realm 沒有那支端點）。 -->
                      <RouterLink
                        v-if="!auth.isReseller"
                        class="slug"
                        :to="{ name: 'site-detail', params: { id: detail.id, siteId: s.id } }"
                        title="站台詳情（授權、子證、指標、站台機密）"
                      >{{ s.slug }}</RouterLink>
                      <span v-else class="slug">{{ s.slug }}</span>
                      <code class="mono">{{ s.instance_id.slice(0, 16) }}</code>
                    </div>
                  </div>
                </td>
                <td>
                  <a v-if="safeHttpUrl(s.public_url)" :href="safeHttpUrl(s.public_url) ?? undefined" target="_blank" rel="noopener">{{ siteDomainText(s) }}</a>
                  <span v-else>{{ siteDomainText(s) }}</span>
                  <!--
                    批135 公開網址：**只顯示、沒有操作鈕**（裁示 89——補網址只靠更新鏈，失敗下次
                    更新自動重試）。代理商看得到同一份（裁示 117），原因只有代碼翻成的一句話。
                    不給連結：公開網址的根路徑沒有頁面（只服務產品的公開頁），點了只會看到 404。
                  -->
                  <div
                    v-if="siteFeature(s, 'pub_url') && (s.pub_domain || s.pub_state !== 'none')"
                    class="publine"
                    :title="pubTitle(s)"
                  >
                    <span class="chip" :class="pubStateChip(s.pub_state)">公開網址 {{ pubStateLabel(s.pub_state) }}</span>
                    <span v-if="s.pub_domain" class="mono sub">{{ s.pub_domain }}</span>
                  </div>
                </td>
                <td>{{ s.customer_name ?? '—' }}</td>
                <td>
                  <div class="hostcell">
                    <span class="status-pill" :class="siteStateClass(s.state)">{{ siteStateLabel(s.state) }}</span>
                    <span class="seen sub" :class="staleLevel(s.last_seen)">心跳 {{ fmtSeen(s.last_seen, { short: true }) }}</span>
                  </div>
                </td>
                <td>
                  <div
                    class="vcell"
                    :class="{ mismatch: s.version_mismatch }"
                    :title="
                      s.version_mismatch
                        ? `agent 盤點 ${s.app_version ?? '—'}、CS 心跳 ${s.cs_app_version ?? '—'} 不一致：升級進行中或盤點資料過期`
                        : undefined
                    "
                  >
                    <span>{{ s.app_version ?? '—' }}</span>
                    <span v-if="s.version_mismatch" class="vmis">≠ CS {{ s.cs_app_version ?? '—' }}</span>
                  </div>
                </td>
                <td class="nowrap">{{ s.port ?? '—' }}</td>
                <td class="nowrap">{{ healthLabel(s.health) }}</td>
                <td class="nowrap">
                  <!-- ★ 沒有憑證時不可以只印一個灰字：`certLevel(null)` 會回 ok（綠），
                       看起來反而像正常。站台只剩 HTTP 是會讓人登不進去的故障。 -->
                  <template v-if="tlsMissing(s.tls_state)">
                    <span
                      class="chip bad"
                      :title="`${s.domain} 沒有憑證，站台只有 HTTP：登入的 Secure cookie 存不下、通路 OAuth 的 https 回址檢查也會失敗。可派「申請憑證」任務補簽。`"
                    >
                      {{ tlsStateLabel(s.tls_state) }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="seen" :class="certLevel(s.cert_expires_at)">{{ fmtSeen(s.cert_expires_at, { short: true }) }}</span>
                    <span class="sub">{{ tlsStateLabel(s.tls_state) }}</span>
                  </template>
                </td>
                <td class="nowrap">
                  <span class="seen" :class="s.backup_status === 'fail' ? 'bad' : ''">
                    {{ fmtSeen(s.backup_latest_at, { short: true }) }}
                  </span>
                  <span class="sub">{{ fmtBytes(s.backup_latest_bytes) }}</span>
                </td>
                <td>
                  <div class="chips">
                    <span v-if="s.needs_setup" class="chip pend">待初始設定</span>
                    <span v-if="!s.updater_deployed" class="chip off" title="host 端沒有 updater unit／spool，線上升級不能派工">
                      未佈更新器
                    </span>
                    <span v-if="s.origin === 'discovered'" class="chip" title="盤點時發現的既有站（非中台開的）">盤點發現</span>
                    <span v-if="s.update_channel" class="chip">{{ s.update_channel }}</span>
                  </div>
                </td>
                <td class="acts-cell">
                  <div class="acts">
                    <UiButton size="sm" @click="openSiteActions(s)">操作</UiButton>
                  </div>
                </td>
              </tr>
              <tr v-if="expandedSites.includes(s.id)" class="subrow">
                <td :colspan="12">
                  <div class="tbl-scroll" data-hscroll>
                    <table v-card-table class="tbl card-table mini">
                      <thead><tr><th>容器</th><th>id</th><th>image</th><th>狀態</th><th>重啟</th></tr></thead>
                      <tbody>
                        <tr v-for="c in s.containers" :key="c.container_id">
                          <td>
                            <span class="slug">{{ c.name }}</span>
                            <span class="sub">{{ c.service || '—' }}</span>
                          </td>
                          <!-- container_id 是完整 64 hex（title 給人複製去貼 docker logs）-->
                          <td><code class="mono" :title="c.container_id">{{ c.container_id.slice(0, 12) }}</code></td>
                          <td><code class="mono">{{ c.image ?? '—' }}</code></td>
                          <td class="nowrap">
                            <span class="status-pill" :class="c.status === 'running' ? 'active' : 'revoked'">
                              {{ c.status }}
                            </span>
                          </td>
                          <td class="nowrap">
                            <span v-if="c.restarts > 0" class="warnnum" :title="`重啟 ${c.restarts} 次`">{{ c.restarts }}</span>
                            <span v-else class="subtle">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 未解決告警 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">未解決告警</h3>
          <span class="count">{{ detail.alerts.length }}</span>
          <span class="spacer" />
          <RouterLink class="tab" :to="{ name: hr('alerts') }">全部告警</RouterLink>
        </div>
        <div v-if="!detail.alerts.length" class="empty"><div class="empty-sub">目前沒有未解決告警。</div></div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead><tr><th>級別</th><th>種類</th><th>對象</th><th>說明</th><th>首次</th><th>最近</th><th></th></tr></thead>
            <tbody>
              <tr v-for="a in detail.alerts" :key="a.id" :class="{ suppressed: a.suppressed }">
                <td><span class="sev" :class="a.severity">{{ severityLabel(a.severity) }}</span></td>
                <td class="nowrap">
                  {{ alertKindLabel(a.kind) }}
                  <span v-if="a.suppressed" class="sub">（被主機離線抑制）</span>
                </td>
                <td class="nowrap">{{ a.site_slug ?? '主機' }}</td>
                <td>{{ alertDetailText(a) }}</td>
                <td class="nowrap tsmall">{{ fmtSeen(a.first_seen, { short: true }) }}</td>
                <td class="nowrap tsmall">{{ fmtSeen(a.last_seen, { short: true }) }}</td>
                <td class="acts-cell">
                  <span v-if="a.acked_at" class="subtle">{{ a.acked_by || '已確認' }}<br />{{ fmtSeen(a.acked_at, { short: true }) }}</span>
                  <UiButton v-else size="sm" @click="ack(a.id)">確認</UiButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 最近任務（真資料：GET /jobs?host_id=）-->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">最近任務</h3>
          <span class="count">{{ jobs.length }}</span>
          <span class="spacer" />
          <RouterLink class="tab" :to="{ name: hr('jobs') }">全部任務</RouterLink>
        </div>
        <div v-if="!jobs.length" class="empty">
          <div class="empty-sub">這台主機還沒有任何任務。站台列的「操作」可派升級／備份／診斷。</div>
        </div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr><th>任務</th><th>站台</th><th>狀態</th><th>階段</th><th>發起人</th><th>耗時</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="j in jobs" :key="j.job_id">
                <td>
                  <div class="hostcell">
                    <RouterLink class="jlink" :to="{ name: hr('job-detail'), params: { jobId: j.job_id } }">
                      {{ jobKindLabel(j.kind) }}
                    </RouterLink>
                    <code class="mono">{{ j.job_id }}</code>
                  </div>
                </td>
                <td class="nowrap">{{ j.site_slug ?? '（主機層）' }}</td>
                <td class="nowrap">
                  <span class="status-pill" :class="jobStateClass(j.state)">{{ jobStateLabel(j.state) }}</span>
                </td>
                <td>
                  <span>{{ jobPhaseLabel(j.phase) }}</span>
                  <span v-if="j.error" class="sub errtext" :title="j.error">{{ j.error }}</span>
                  <span v-else-if="j.detail" class="sub" :title="j.detail">{{ j.detail }}</span>
                </td>
                <td class="nowrap">{{ j.requested_by ?? '—' }}</td>
                <td class="nowrap">{{ fmtDuration(jobElapsed(j, jobsNow)) }}</td>
                <td class="acts-cell">
                  <div class="acts">
                    <RouterLink class="tab detail-link" :to="{ name: hr('job-detail'), params: { jobId: j.job_id } }">
                      詳情
                    </RouterLink>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- 站台操作（依角色顯示；回收另有打字確認）-->
    <UiModal
      :open="actOpen"
      :title="actSite ? `站台操作 — ${actSite.slug}` : '站台操作'"
      width="680px"
      @close="closeSiteActions"
    >
      <template v-if="actSite">
        <div class="sinfo">
          <div class="mi"><span class="mk">網域</span><span class="mv">{{ actSite.domain ?? '—' }}</span></div>
          <div class="mi">
            <span class="mk">狀態</span>
            <span class="mv">{{ siteStateLabel(actSite.state) }}・健康 {{ healthLabel(actSite.health) }}</span>
          </div>
          <div class="mi"><span class="mk">版本</span><span class="mv">{{ actSite.app_version ?? '—' }}</span></div>
          <div class="mi"><span class="mk">客戶</span><span class="mv">{{ actSite.customer_name ?? '—' }}</span></div>
        </div>

        <!-- 回收確認面板 -->
        <template v-if="decomOpen">
          <div class="decom">
            <p class="dwarn">
              ⚠️ <strong>回收是不可逆的</strong>：agent 會先打包最終備份，然後把這站銷毀
              （唯一會 <code class="mono">compose down</code> 的路徑），中台同時撤銷授權、標記主機連結退役、
              刪掉 DNS 記錄。<span class="dkeep">最終備份會保留在該台 VM 上：自建立起滿下方的保留天數（留空＝30 天）後
              24 小時內自動刪除（agent 0.2.16 起；主機離線期間順延到恢復連線後執行）。</span>其餘資料不會回來。
            </p>
            <label class="field">
              <span>請輸入完整網域「{{ actSite.domain }}」以確認</span>
              <input v-model="decomDomain" :placeholder="actSite.domain ?? ''" />
            </label>
            <label class="field">
              <span>最終備份保留天數（1～3650；留空＝預設 30 天）</span>
              <input
                v-model="decomDays"
                class="decom-days"
                type="number"
                :min="DECOM_DAYS_MIN"
                :max="DECOM_DAYS_MAX"
                step="1"
                placeholder="30"
              />
            </label>
            <p v-if="decomDaysError" class="err decom-days-err">{{ decomDaysError }}</p>
          </div>
          <div class="dacts">
            <UiButton @click="decomOpen = false">返回</UiButton>
            <UiButton
              variant="danger"
              :disabled="!decomReady"
              :loading="actBusy === 'decommission_site'"
              @click="doDecommission"
            >
              永久回收這個站台
            </UiButton>
          </div>
        </template>

        <template v-else>
          <ul class="alist">
            <!-- `act-<kind>` 是給測試定位用的（這一排的按鈕文字會隨文案調整，
                 用文字找列＝每次改字就紅一支測試）。 -->
            <li v-for="a in siteActions" :key="a.key" :class="[`act-${a.key}`, { off: !!a.blocked }]">
              <div class="atext">
                <span class="alabel">{{ a.label }}</span>
                <span class="adesc">{{ a.desc }}</span>
                <span v-if="a.blocked" class="ablock">不能執行：{{ a.blocked }}</span>
              </div>
              <UiButton
                :variant="a.variant"
                size="sm"
                :disabled="!!a.blocked"
                :loading="actBusy === a.key"
                @click="runSiteAction(a)"
              >
                {{ a.label }}
              </UiButton>
            </li>
          </ul>

          <!-- 瀏覽器擋掉新視窗時的備援：直接把票的網址列出來 -->
          <div v-if="canSso && ssoTicket" class="field ssobox">
            <div class="field-label">瀏覽器擋掉了新視窗——請點這個連結進站（{{ ssoTicket.expires_at }} 前有效）</div>
            <a v-if="safeHttpUrl(ssoTicket.url)" :href="safeHttpUrl(ssoTicket.url) ?? undefined" target="_blank" rel="noopener" class="ssolink">{{ ssoTicket.url }}</a>
            <span v-else class="ssolink">{{ ssoTicket.url }}</span>
          </div>

          <!-- vendor SSO 稽核（批99-B：**代理商完全不渲染**——後端沒有這支端點）
               批141：產品不支援供應商登入的站也不渲染。 -->
          <template v-if="canSso && siteFeature(actSite, 'vendor_sso')">
            <h4 class="sub-title">最近的供應商登入紀錄</h4>
            <p v-if="!grants.length" class="subtle">目前沒有紀錄（或尚未載入）。</p>
            <div v-else class="tbl-scroll" data-hscroll>
              <table v-card-table class="tbl card-table mini">
                <thead><tr><th>簽發人</th><th>簽發時間</th><th>使用時間</th><th>來源 IP</th></tr></thead>
                <tbody>
                  <tr v-for="g in grants" :key="g.jti">
                    <td>{{ g.issued_by ?? '—' }}</td>
                    <td class="nowrap">{{ fmtSeen(g.issued_at, { short: true }) }}</td>
                    <td class="nowrap">{{ g.used_at ? fmtSeen(g.used_at, { short: true }) : '未使用' }}</td>
                    <td class="nowrap">{{ g.used_ip ?? '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="subtle">
              這份紀錄只存在中台——站台端查不到我方人員的登入痕跡（票的內容刻意不含員工 email）。
            </p>
          </template>
        </template>
      </template>
      <template #foot>
        <UiButton @click="closeSiteActions">關閉</UiButton>
      </template>
    </UiModal>

    <!-- 重發後的一次性註冊碼 -->
    <!-- 主機設定對話框（主人裁示：不在頁面本體放整張表單；operator 沒有入口——後端 require_admin 是真閘門）-->
    <!-- 主機服務（批169）：安裝／升級可指定版本與更新通道（都留空＝最新版＋服務預設通道）；移除另有確認框 -->
    <UiModal :open="hsvcOpen" :title="hsvcTitle" width="520px" @close="hsvcOpen = false">
      <template v-if="hsvcTarget">
        <p v-if="hsvcAction === 'remove'" class="muted">
          停止服務並刪除它的 image 與版本目錄；各站的連線目錄保留（重新安裝後自動接上）。
        </p>
        <template v-else>
          <p class="muted">
            由 agent 從下載站取包、驗簽章、載入 image 後啟動；新版起不來會自動退回前一版。
            同一台主機有其他任務在跑時不能派。
          </p>
          <div class="form-grid">
            <label class="field">
              <span>版本</span>
              <input v-model="hsvcVersion" type="text" placeholder="留空＝最新版" autocomplete="off" />
            </label>
            <label class="field">
              <span>更新通道</span>
              <input
                v-model="hsvcChannel"
                type="text"
                :placeholder="hsvcTarget.default_channel ? `留空＝${hsvcTarget.default_channel}` : '留空＝服務預設'"
                autocomplete="off"
              />
            </label>
          </div>
          <p v-if="hsvcError" class="err">{{ hsvcError }}</p>
        </template>
      </template>
      <template #foot>
        <UiButton variant="secondary" @click="hsvcOpen = false">取消</UiButton>
        <UiButton
          :variant="hsvcAction === 'remove' ? 'danger' : 'primary'"
          :disabled="hsvcBusy || !!hsvcError"
          @click="submitHostService"
        >
          {{ HOST_SERVICE_ACTION_LABELS[hsvcAction] }}
        </UiButton>
      </template>
    </UiModal>

    <!-- 改站台設定（批112）：只列這個產品宣告過、且非秘密的欄位 -->
    <UiModal
      :open="siteConfigOpen"
      :title="actSite ? `改設定 — ${actSite.slug}` : '改設定'"
      width="560px"
      @close="siteConfigOpen = false"
    >
      <template v-if="actSite">
        <p class="muted">
          會改寫該站 <code>.env</code> 的對應鍵值並重建容器（api 與 worker 都會重新讀取）。
          留空的欄位不送出。
        </p>
        <div class="form-grid">
          <label v-for="f in siteConfigFields(actSite)" :key="f.env_key" class="field">
            <span>{{ f.label }}<small class="envkey"> {{ f.env_key }}</small></span>
            <select v-if="f.type === 'bool'" v-model="siteConfigDraft[f.env_key]">
              <option value="true">開啟</option>
              <option value="false">關閉</option>
            </select>
            <input
              v-else
              v-model="siteConfigDraft[f.env_key]"
              :type="f.type === 'number' ? 'number' : 'text'"
              :min="f.min ?? undefined"
              :max="f.max ?? undefined"
            />
            <small v-if="f.hint" class="muted">{{ f.hint }}</small>
          </label>
        </div>
      </template>
      <template #foot>
        <UiButton variant="secondary" @click="siteConfigOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="siteConfigBusy" @click="submitSiteConfig">
          派送
        </UiButton>
      </template>
    </UiModal>

    <UiModal
      :open="attachOpen"
      :title="attachEditing ? '改下發範圍' : '掛載設定樣板'"
      width="560px"
      @close="attachOpen = false"
    >
      <p class="muted">
        設定樣板{{ auth.isManagerOrAbove ? '' : '由營運主管' }}在〈產品 › 設定樣板〉維護（跨主機共用）。掛上來之後，這台主機上符合範圍的
        站台就會在開站／〈改設定〉時拿到這組環境變數。
      </p>
      <div class="form-grid">
        <label class="field">
          <span>樣板</span>
          <select v-model="attachTemplateId" :disabled="!!attachEditing">
            <option v-if="attachEditing" :value="attachTemplateId">
              {{ detail?.agent_config?.find((b) => b.template_id === attachEditing)?.name }}
            </option>
            <option v-for="t in attachableTemplates" :key="t.id" :value="t.id">
              {{ t.name }}（{{ t.namespace }}／{{ t.fields.length }} 欄）
            </option>
          </select>
          <small v-if="!attachEditing && !attachableTemplates.length" class="muted">
            <template v-if="auth.isManagerOrAbove">沒有可掛載的樣板——先到〈產品 › 設定樣板〉建立或匯入。</template>
            <template v-else>沒有可掛載的樣板——請營運主管先在〈產品 › 設定樣板〉建立或匯入。</template>
          </small>
        </label>
        <label class="field">
          <span>下發範圍</span>
          <label class="cb">
            <input
              type="checkbox"
              :checked="attachProducts == null"
              @change="attachProducts = attachProducts == null ? [] : null"
            />
            全部產品（預設）
          </label>
          <div v-if="attachProducts != null" class="scope-list">
            <label v-for="k in mountableProducts" :key="k" class="cb">
              <input
                type="checkbox"
                :checked="attachProducts.includes(k)"
                @change="toggleAttachProduct(k)"
              />
              {{ store.productName(k) }}
            </label>
            <small v-if="detail?.agent_products == null" class="muted">
              這台 agent 尚未回報支援哪些產品（請升級 agent）——請先用「全部產品」。
            </small>
            <small v-else-if="!mountableProducts.length" class="muted">
              這台主機支援的產品都不吃主機設定（產品的代管設定沒開「主機設定下發」）。
            </small>
            <small v-else class="muted">只列出吃主機設定的產品（產品代管設定的「主機設定下發」）。</small>
          </div>
        </label>
      </div>
      <template #foot>
        <UiButton variant="secondary" @click="attachOpen = false">取消</UiButton>
        <UiButton
          variant="primary"
          :disabled="attachBusy || !attachTemplateId"
          @click="submitAttach"
        >
          {{ attachEditing ? '儲存' : '掛載' }}
        </UiButton>
      </template>
    </UiModal>

    <!-- 單次預約更新（批192） -->
    <UiModal :open="onceOpen" title="預約更新" width="640px" @close="onceOpen = false">
      <div class="form-grid">
        <p class="subtle">
          一律更新到最新版；全部代管站依序一次一站；某站失敗會略過並告警，回滾也失敗時才暫停。
        </p>
        <label class="field">
          <span>預約時間（{{ planTz }}）</span>
          <input v-model="onceAt" type="datetime-local" />
        </label>
        <div class="field">
          <div class="field-label">站台與順序（已勾 {{ onceSites.length }}／{{ onceOrder.length }}）</div>
          <ul class="ord-list">
            <li v-for="(id, i) in onceOrder" :key="id" class="ord-row">
              <label class="cb">
                <input type="checkbox" :checked="oncePicked.includes(id)" @change="toggleOncePick(id)" />
                <code class="mono">{{ siteSlug(id) }}</code>
              </label>
              <span class="spacer" />
              <button type="button" class="ord-btn" :disabled="i === 0" title="上移" @click="onceOrder = moveItem(onceOrder, i, -1)">↑</button>
              <button type="button" class="ord-btn" :disabled="i === onceOrder.length - 1" title="下移" @click="onceOrder = moveItem(onceOrder, i, 1)">↓</button>
            </li>
          </ul>
        </div>
        <div class="field">
          <label class="cb">
            <input v-model="oncePlugins" type="checkbox" :disabled="!plan?.plugins_supported" />
            一併更新擴充套件
          </label>
          <p v-if="plan && !plan.plugins_supported" class="subtle">這台主機或產品不支援順帶更新擴充套件。</p>
        </div>
        <label class="field">
          <span>最長執行時間（分鐘）</span>
          <input v-model="onceMax" type="number" min="30" max="720" />
          <p class="subtle">超過這段時間還沒輪到的站台會被略過並告警（30 到 720，預設 180）。</p>
        </label>
        <p v-if="onceError" class="err">{{ onceError }}</p>
      </div>
      <template #foot>
        <UiButton @click="onceOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="onceBusy" @click="submitOnce">排入預約</UiButton>
      </template>
    </UiModal>

    <!-- 固定維護時段（批192） -->
    <UiModal :open="winOpen" title="固定維護時段" width="640px" @close="winOpen = false">
      <div class="form-grid">
        <p class="subtle">
          到了時段會自動把全部代管站依序更新到最新版，一次一站；某站失敗會略過並告警，回滾也失敗時才暫停。
        </p>
        <div class="field">
          <label class="cb"><input v-model="win.enabled" type="checkbox" /> 啟用這個維護時段</label>
        </div>
        <div class="field">
          <div class="field-label">星期（{{ planTz }}）</div>
          <div class="wd-row">
            <label v-for="d in WEEKDAYS" :key="d.value" class="cb wd">
              <input type="checkbox" :checked="win.weekdays.includes(d.value)" @change="toggleWeekday(d.value)" />
              {{ d.label }}
            </label>
          </div>
        </div>
        <div class="field-row two">
          <label class="field">
            <span>開始時間（{{ planTz }}）</span>
            <input v-model="win.time" type="time" />
          </label>
          <label class="field">
            <span>最長執行時間（分鐘）</span>
            <input v-model="winMax" type="number" min="30" max="720" />
          </label>
        </div>
        <div class="field">
          <label class="cb">
            <input v-model="win.include_plugins" type="checkbox" :disabled="!plan?.plugins_supported" />
            一併更新擴充套件
          </label>
          <p v-if="plan && !plan.plugins_supported" class="subtle">這台主機或產品不支援順帶更新擴充套件。</p>
        </div>
        <div class="field">
          <div class="field-label">站台順序</div>
          <ul class="ord-list">
            <li v-for="(id, i) in win.site_order" :key="id" class="ord-row">
              <code class="mono">{{ siteSlug(id) }}</code>
              <span class="spacer" />
              <button type="button" class="ord-btn" :disabled="i === 0" title="上移" @click="win.site_order = moveItem(win.site_order, i, -1)">↑</button>
              <button type="button" class="ord-btn" :disabled="i === win.site_order.length - 1" title="下移" @click="win.site_order = moveItem(win.site_order, i, 1)">↓</button>
            </li>
          </ul>
          <p v-if="!win.site_order.length" class="subtle">這台主機上沒有可升級的站台。</p>
        </div>
        <p v-if="winError" class="err">{{ winError }}</p>
      </div>
      <template #foot>
        <UiButton @click="winOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="winBusy" @click="submitWindow">儲存</UiButton>
      </template>
    </UiModal>

    <UiModal :open="settingsOpen" title="主機設定" width="640px" @close="settingsOpen = false">
      <div class="form-grid">
        <div class="field-row two">
          <label class="field">
            <span>狀態</span>
            <select v-model="form.status">
              <option value="pending">待註冊</option>
              <option value="active">運行中</option>
              <option value="draining">排空中（不再開新站）</option>
              <option value="retired">已退役</option>
            </select>
          </label>
          <label class="field">
            <span>站數上限</span>
            <input v-model="form.max_sites" type="number" min="1" placeholder="留空＝只看資源" />
          </label>
        </div>
        <!-- 歸屬（批99-B，我方視角專用）。`resellersDenied`＝admin 讀不到代理商清單
             （`GET /resellers` 是 superadmin 端點）→ 退化成唯讀顯示，不給改。
             代理商 realm 的 `PUT /hosts/{id}` 根本不收 `reseller_id`，所以他這裡沒有這一欄。 -->
        <template v-if="!auth.isReseller">
          <label v-if="!store.resellersDenied" class="field">
            <span>代理商</span>
            <select v-model="form.reseller_id">
              <option value="">愛+1 自營（不掛代理商）</option>
              <option v-for="r in store.resellers" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
            <p class="subtle">
              改歸屬時，若這台上還有在役站台而客戶不屬新代理商，後端會擋下（422）。
            </p>
          </label>
          <div v-else class="field">
            <div class="field-label">歸屬</div>
            <div class="subtle">
              {{ detail?.reseller_id ? `代理商：${detail?.reseller_name ?? detail?.reseller_id}` : '愛+1 自營' }}
              ——需超級管理者才能改。
            </div>
          </div>
        </template>
        <div class="field">
          <div class="field-label">獨佔</div>
          <div class="excl-row">
            <label class="cb"><input v-model="form.exclusive" type="checkbox" /> 只給單一客戶</label>
            <select v-model="form.exclusive_customer_id" :disabled="!form.exclusive">
              <option value="">首個開站客戶自動填入</option>
              <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: !auth.isReseller }) }}</option>
            </select>
          </div>
          <p class="subtle">取消獨佔會一併鬆開綁定的客戶。</p>
        </div>
        <label class="field">
          <span>標籤（JSON 物件）</span>
          <textarea v-model="form.labels" rows="3" placeholder='{"tier":"prod"}' class="mono-in" />
        </label>
        <label class="field">
          <span>備註</span>
          <textarea v-model="form.notes" rows="3" />
        </label>
        <p v-if="editError" class="err">{{ editError }}</p>
      </div>
      <template #foot>
        <UiButton :disabled="!dirty" @click="syncForm">還原</UiButton>
        <UiButton variant="primary" :disabled="!dirty" :loading="busy" @click="save">儲存變更</UiButton>
      </template>
    </UiModal>

    <UiModal :open="enrollOpen" title="新的主機註冊碼（只顯示一次）" width="640px" @close="closeEnroll">
      <EnrollCodeCard v-if="enroll" :enroll="enroll" :host-name="detail?.name" />
      <template #foot>
        <UiButton variant="primary" @click="closeEnroll">我已複製，關閉</UiButton>
      </template>
    </UiModal>

    <!-- 換機：整批重簽授權（先試算，確認後才真的簽） -->
    <UiModal :open="reOpen" title="換機：整批重簽授權" width="860px" @close="reOpen = false">
      <div v-if="rePreview" class="relic">
        <p class="relic-lead">
          把這台主機上<strong>所有站台</strong>的授權，重簽到主機<strong>目前</strong>的機器錨點。
          VM 遷移／擴容／重建會換掉錨點，站台的授權指紋就對不上，CS 端會把站台轉成唯讀。
        </p>
        <p class="relic-lead subtle">
          主機目前錨點：<span class="mono">{{ reAnchorKeys.join('、') || '（無）' }}</span>
          （只列鍵名——錨點值是綁機用的密鑰材料）
        </p>
        <div v-if="rePreview.sites.length" class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table mini">
            <thead>
              <tr><th>站台</th><th>方案</th><th>配額</th><th>到期</th><th>指紋狀態</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in rePreview.sites" :key="row.instance_id">
                <td>{{ row.slug }}</td>
                <td>{{ row.plan || '客製' }}</td>
                <!-- 批114：原本印證上單一的數字欄。試算列沒帶產品，拿不到憑證設計 →
                     spec 傳 null，`QuotaSummary` 會退成「原始鍵名 值」（不猜語意）。 -->
                <td class="requota">
                  <QuotaSummary :spec="null" :quotas="row.quotas ?? null" />
                </td>
                <td>{{ row.expires_at ? new Date(row.expires_at).toLocaleDateString('zh-TW') : '未定' }}</td>
                <td>
                  <span class="status-pill" :class="row.matches ? 'active' : 'revoked'">
                    {{ row.matches ? '已符合' : '需重簽' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="subtle">這台主機上沒有可重簽的站台。</p>
        <ul v-if="rePreview.skipped.length" class="relic-skip">
          <li v-for="sk in rePreview.skipped" :key="sk.slug + sk.reason">
            略過 <strong>{{ sk.slug }}</strong>——{{ reSkipLabel(sk.reason) }}
          </li>
        </ul>
        <p v-if="rePreview.to_resign > 0" class="relic-warn">
          ⚠️ 重簽後 agent 約 <strong>60 秒</strong>內會自動換證並重啟該站 api，站台會短暫中斷；
          <strong>已符合的站不動</strong>。instance_id 與客戶連結都不變。
        </p>
        <p v-else class="relic-ok">全部站台指紋已符合，不需重簽。</p>
      </div>
      <template #foot>
        <UiButton @click="reOpen = false">關閉</UiButton>
        <UiButton
          variant="primary"
          :disabled="!rePreview || rePreview.to_resign === 0"
          :loading="reBusy"
          @click="runRelicense"
        >
          {{ rePreview && rePreview.to_resign > 0 ? `重簽 ${rePreview.to_resign} 個站台` : '不需重簽' }}
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.crumb {
  margin-bottom: var(--sp-3);
  font-size: var(--fs-12);
}
.crumb a {
  color: var(--fg-3);
  text-decoration: none;
}
.crumb a:hover {
  color: var(--fg);
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.head-top {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.relic {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}
.relic-lead {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--fg-2);
  line-height: 1.7;
}
.relic-skip {
  margin: 0;
  padding-left: var(--sp-4);
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.relic-warn,
.relic-ok {
  margin: 0;
  padding: var(--sp-3);
  border-radius: var(--r-md);
  font-size: var(--fs-12);
  line-height: 1.7;
}
.relic-warn {
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
}
.relic-ok {
  background: var(--st-active-bg);
  color: var(--st-active-fg);
}
.relic .mono {
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  overflow-wrap: anywhere;
}
.head-top h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.spacer {
  flex: 1;
}
.meta {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: var(--sp-2) var(--sp-4);
  margin-top: var(--sp-4);
}
.mi {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.mk {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.mv {
  font-size: var(--fs-13);
  color: var(--fg);
  overflow-wrap: anywhere;
}
/* 主機服務（批169）：一個服務一塊；標題列與 `card-head` 同一種排法，細節沿用 `.meta` 鍵值格。 */
.hsvc-list {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}
.hsvc + .hsvc {
  border-top: 1px solid var(--border);
  padding-top: var(--sp-4);
}
.hsvc-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
}
.hsvc-name {
  font-size: var(--fs-14);
}
.hsvc .meta {
  margin-top: var(--sp-3);
}
.hsvc-issues {
  margin: var(--sp-3) 0 0;
  padding-left: 1.2em;
  font-size: var(--fs-12);
  color: var(--warning);
}
/* 支援產品 chip（批99-A）：版面沿用 ui.css 的 `.chips`，只補對齊與字級。 */
.prodchips {
  align-items: center;
  gap: 4px;
  font-size: var(--fs-11);
}
/* 主機資訊：可縮合鍵值表（取代原本一排膠囊）。兩組鍵值一列、窄螢幕退成一組。 */
.hinfo {
  margin-top: var(--sp-3);
}
.hinfo-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px 2px 2px;
  border: 0;
  border-radius: var(--r-sm);
  background: none;
  color: var(--fg-2);
  font: inherit;
  font-size: var(--fs-12);
  cursor: pointer;
}
.hinfo-toggle:hover {
  background: var(--bg-hover);
}
.hinfo-toggle .tri {
  display: inline-block;
}
.kv-grid {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) max-content minmax(0, 1fr);
  gap: 4px var(--sp-4);
  margin-top: var(--sp-2);
  padding: var(--sp-3);
  border: 1px solid var(--border-2);
  border-radius: var(--r-sm);
  background: var(--bg-sunken);
  font-size: var(--fs-12);
}
.kv-k {
  color: var(--fg-4);
  white-space: nowrap;
}
.kv-v {
  color: var(--fg);
  overflow-wrap: anywhere;
}
.kv-v.wide {
  grid-column: 2 / -1;
}
@media (max-width: 900px) {
  .kv-grid {
    grid-template-columns: max-content minmax(0, 1fr);
  }
  .kv-v.wide {
    grid-column: 2;
  }
}
.section-title.clickable {
  cursor: pointer;
}
.chip.excl {
  background: var(--st-transferred-bg);
  border-color: transparent;
  color: var(--st-transferred-fg);
  font-weight: 600;
}
/* 歸屬膠囊（批99-B，與 HostsView 同一套配色）；自營是純文字不上膠囊。 */
.chip.owner {
  background: var(--brand-100);
  border-color: transparent;
  color: var(--brand-700);
  font-weight: 600;
}
[data-theme='dark'] .chip.owner {
  background: var(--bg-active);
  color: var(--brand-400);
}
.chip.pend {
  background: var(--st-pending-bg);
  border-color: transparent;
  color: var(--st-pending-fg);
  font-weight: 600;
}
.chip.off {
  background: var(--st-closed-bg);
  border-color: transparent;
  color: var(--st-closed-fg);
}
/* 憑證未簽發＝站台只剩 HTTP。沿用 `.seen.bad` 的 `--danger`，一眼看得出是故障不是狀態。 */
.chip.bad {
  border-color: var(--danger);
  color: var(--danger);
  font-weight: 600;
}
.chip.ok {
  background: var(--st-active-bg);
  border-color: transparent;
  color: var(--st-active-fg);
}
/* 批135：網域欄下方的公開網址一行（只顯示）。 */
.publine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}
.res-grid {
  display: grid;
  grid-template-columns: minmax(220px, 320px) 1fr;
  gap: var(--sp-5);
  align-items: start;
}
.meters > * + * {
  margin-top: 6px;
}
.forecast {
  margin: var(--sp-3) 0 0;
  font-size: var(--fs-12);
  color: var(--warning);
}
.tbl.mini {
  font-size: var(--fs-11);
}
.mcell {
  min-width: 120px;
}
.hostcell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
}
.slug {
  font-weight: 600;
}

/* --- image 卡（批80 批B1）--- */
.iname {
  font-weight: 600;
}
.iname.dangling {
  color: var(--fg-4);
  font-weight: 400;
}
.more-row {
  margin-top: var(--sp-2);
}

/* --- 站台容器子列（批80 批B1）--- */
.slugrow {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.tri {
  width: 16px;
  flex: 0 0 16px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--fg-4);
  font-size: 9px;
  line-height: 18px;
  cursor: pointer;
  transition: transform var(--dur-base) var(--ease);
}
.tri.open {
  transform: rotate(90deg);
  color: var(--fg-2);
}
/* 沒有容器的站也占同樣寬度＝slug 欄不會左右跳動。 */
.tri.ph {
  cursor: default;
}
.subrow > td {
  background: var(--bg-sunken);
  padding: var(--sp-2) var(--sp-3);
}
.warnnum {
  color: var(--warning);
  font-weight: 600;
}
/* 批114：資訊列裡的警示小字（例：agent 尚未回報支援產品）。尺寸與 `.subtle` 同一階，
   只換顏色——這不是錯誤，是「該去做一件事」。 */
.warn-note {
  color: var(--warning);
  font-size: var(--fs-12);
  font-weight: 600;
  cursor: help;
}
/* `.sub` 只給 td **內**的 span（display:block 套在 td 本身會讓儲存格脫離表格列）；
   儲存格本身的小字用 `.tsmall`。 */
.sub {
  font-size: var(--fs-11);
  color: var(--fg-4);
  display: block;
}
.tsmall {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.vcell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  white-space: nowrap;
}
.vcell.mismatch {
  color: var(--warning);
  font-weight: 600;
}
.vmis {
  font-size: var(--fs-11);
}
.nowrap {
  white-space: nowrap;
}
.notes {
  margin: var(--sp-3) 0 0;
  font-size: var(--fs-12);
  color: var(--fg-2);
  white-space: pre-wrap;
}
.acts {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
}
.field-row.two {
  display: grid;
  /* 批171：`1fr` 是 `minmax(auto, 1fr)`——右格的最小寬度一大（獨佔那列的客戶下拉＝最長的客戶名），
     左格就被壓到一兩個字寬（「站／數／上／限」直排、數字框約 25px）、整排還超出對話框右緣。 */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--sp-3);
}
.excl-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}
.excl-row select {
  /* 批171：原生 select 的最小寬度＝最長的選項（客戶名），`flex: 1` 仍會把它算進整列的最小寬度
     → 基準寬 0、靠 flex-grow 撐滿，最小寬度就不再被選項長度綁住。 */
  flex: 1 1 0;
  width: 0;
  min-width: 0;
}
.cb {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  white-space: nowrap;
}
.scope-list {
  display: grid;
  gap: 4px;
  padding-left: var(--sp-4);
}
.mono-in {
  font-family: var(--font-mono);
  font-size: var(--fs-11);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
tr.suppressed {
  opacity: 0.6;
}

/* --- 批次升級列 --- */
.batchbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px dashed var(--border-strong);
  border-radius: var(--r-md);
}
.batchbar .ver {
  width: 180px;
}
.pick-h,
td.pick {
  width: 1%;
  white-space: nowrap;
}

/* --- 預約更新（批192）--- */
.upd-window {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-6);
  margin-bottom: var(--sp-3);
}
.upd-warn {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  font-size: var(--fs-12);
  color: var(--warning);
  border: 1px solid var(--warning);
  border-radius: var(--r-md);
}
.upd-run {
  padding: var(--sp-3) 0;
  border-top: 1px solid var(--border);
}
.upd-run.paused {
  border-top-color: var(--danger);
}
.upd-run-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-13);
}
.upd-items {
  list-style: none;
  margin: var(--sp-2) 0 0;
  padding: 0;
  display: grid;
  gap: 4px;
}
.upd-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
}
.upd-res {
  overflow-wrap: anywhere;
}
.ord-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 4px;
}
.ord-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 4px var(--sp-2);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
}
.ord-btn {
  min-width: 28px;
  height: 28px;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-md);
  background: var(--bg);
  color: var(--fg-2);
  cursor: pointer;
}
.ord-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.wd-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
}
.wd {
  font-size: var(--fs-13);
}

/* --- 任務表 --- */
.jlink {
  color: var(--brand-700);
  font-weight: 600;
  text-decoration: none;
}
[data-theme='dark'] .jlink {
  color: var(--brand-400);
}
.jlink:hover {
  text-decoration: underline;
}
.sub.errtext {
  color: var(--danger);
}
.sub {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.detail-link {
  padding: 4px 10px;
  font-size: var(--fs-11);
}

/* --- 站台操作對話框 --- */
.sinfo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--sp-2) var(--sp-4);
  padding-bottom: var(--sp-3);
  margin-bottom: var(--sp-3);
  border-bottom: 1px solid var(--border-2);
}
.alist {
  list-style: none;
  margin: 0;
  padding: 0;
}
.alist li {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) 0;
  border-bottom: 1px solid var(--border-2);
}
.alist li:last-child {
  border-bottom: none;
}
.alist li.off {
  opacity: 0.6;
}
.atext {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.alabel {
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg);
}
.adesc {
  font-size: var(--fs-11);
  color: var(--fg-4);
  line-height: var(--lh-base);
}
.ablock {
  font-size: var(--fs-11);
  color: var(--danger);
}
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg-3);
}
.ssobox {
  margin-top: var(--sp-3);
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: var(--st-pending-bg);
}
.ssolink {
  font-size: var(--fs-11);
  word-break: break-all;
  color: var(--brand-700);
}
[data-theme='dark'] .ssolink {
  color: var(--brand-400);
}
.decom {
  display: grid;
  gap: var(--sp-3);
}
.dwarn {
  margin: 0;
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: rgba(209, 67, 67, 0.1);
  color: var(--danger);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.dacts {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-4);
}
/* 手機／平板（批171）：資源水位的右欄 `1fr` 會被掛載點表的最小寬度撐開、超出卡片 → 釘成 `minmax(0, 1fr)`。 */
@media (--compact) {
  .res-grid {
    grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  }
}
/* 手機（批171）：資源水位上下排；「主機資訊」摺疊鈕的摘要換到下一行（不把「主機資訊」四個字擠成兩行）。 */
@media (--mobile) {
  .res-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .hinfo-toggle {
    flex-wrap: wrap;
    text-align: left;
    white-space: nowrap;
  }
  .hinfo-toggle .subtle {
    flex: 1 1 100%;
    padding-left: 18px;
    white-space: normal;
  }
}
</style>
